// HomeBeta — press-first homepage proposal at /home-beta.
// Not linked from nav, not in sitemap, noindex'd. Iterate here until approved,
// then swap into Index.tsx when ready.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ensureModelViewer } from '@/lib/ensureModelViewer';
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { isNativeApp } from "@/lib/platform";
import { Trans, useTranslation } from "react-i18next";
import {
  Activity, ArrowRight, ArrowUpRight, Baby, Bone, Brain, Building2, ChevronLeft,
  ChevronRight, Database, Maximize2, Dna, DollarSign, Download, Droplet, ExternalLink,
  Flame, Globe, HandHeart, Heart, HeartPulse, Layers, MessageCircle, Monitor, Pill,
  Play, Quote, Replace, Ribbon, Scissors, Search, Share2, Shield, ShieldCheck, Smile,
  Sparkles, Stethoscope, TrendingUp, Users, Wind,
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { DignityNarrationPlayer } from "@/components/dignity/DignityNarrationPlayer";
import { ShareDignityModal } from "@/components/ShareDignityModal";
import { ProcedureDrilldownModal } from "@/components/ProcedureDrilldownModal";
import { ScenarioCard, type ScenarioCardData, type CardTheme } from "@/components/ScenarioCard";
import { ScenarioFamilyModal, type RawScenario as FamilyModalScenario } from "@/components/ScenarioFamilyModal";
import { CategoryBrowseRow } from "@/components/CategoryBrowseRow";
import { HeroSearchPill } from "@/components/home-beta/HeroSearchPill";
import { SCENARIO_CATEGORIES, scenarioMatchesCategory } from "@/config/scenarioCategories";
import { useViewportTier } from "@/hooks/useViewportTier";
import { useTheme } from "@/contexts/ThemeContext";
import { COUNTRY_SLUGS } from "@/lib/countryUtils";
import { loadStaticData, loadTranslatedStaticData } from "@/lib/offlineData";
import { supabase } from "@/integrations/supabase/client";
import { scenarioStorage } from "@/lib/scenarioStorage";
import { useAppReviewFlags } from "@/config/appReviewFlags";
import { useBrand } from "@/config/brands";
import { titleCaseHospital } from "@/lib/hospitalName";

// Icon per scenario category — mirrors HospitalScenarioDeals on the map so the
// home-beta pill bar reads identically to the hospital scenario browser users
// already know from /hospital/:id and /map/:state/:hospital.
const SCENARIO_CATEGORY_ICONS: Record<string, React.ElementType> = {
  emergency: Flame, orthopedic: Bone, spine: Activity, surgery: Scissors,
  "womens-health": Baby, cardiology: Heart, cancer: Ribbon, immune: Shield,
  diabetes: Pill, pulmonology: Wind, transplant: Replace, "gene-therapy": Dna,
};

// Curated featured scenarios — surfaced as the default "Featured" pill so the
// landing experience leads with consumer-recognizable life events rather than
// a dump of every category. UUIDs are real anchors from scenarios_index.json.
const FEATURED_SCENARIO_UUIDS = new Set([
  "550e8400-e29b-41d4-a716-446655440002", // C-Section Delivery
  "0c4dda28-32fa-41c7-8dc7-b8afb3d0f7cd", // Appendectomy (Emergency)
  "493b56a6-5a26-489f-a9b8-85b3761e6431", // Total Knee Replacement
  "d31d9939-a403-4fbf-8988-0ccd6da5732b", // Childhood Asthma — First-Year Management
  "550e8400-e29b-41d4-a716-446655440003", // Heart Attack Treatment
  "762d493a-e6ad-4656-80ff-70a0bae338e7", // Cataract Surgery
  "968c0657-7878-4499-a2ba-5103489e3020", // Mammogram (Screening)
  "3f0682b3-4dfc-42ca-8bce-5492280bc399", // Gallbladder Removal (Laparoscopic)
]);

// ─────────────────────────────────────────────────────────────────────────────
// Theme — patriotic AHP palette, light/dark aware. Kept local so the beta
// page can iterate independently of the main homepage's 4k-line theme builder.
// ─────────────────────────────────────────────────────────────────────────────
const serif = "'Instrument Serif', Georgia, 'Times New Roman', serif";
const sans = "'Instrument Sans', system-ui, -apple-system, sans-serif";

function useThemeTokens() {
  const { isDark } = useTheme();
  return useMemo(() => {
    if (isDark) {
      return {
        bg: "#0a1628",
        bgAlt: "#050e1a",
        bgInverse: "#FAF9F6",
        text: "#FAF9F6",
        textOnInverse: "#1B1B1B",
        textMuted: "rgba(250,249,246,0.6)",
        textMutedOnInverse: "rgba(27,27,27,0.62)",
        gold: "#C5A55A",
        red: "#B22234",
        blue: "#5B9BD5",
        navy: "#002868",
        cardBg: "#0d1e35",
        cardBorder: "rgba(250,249,246,0.10)",
        cardBgOnInverse: "#ffffff",
        cardBorderOnInverse: "rgba(27,27,27,0.08)",
        divider: "rgba(250,249,246,0.10)",
        chipBg: "rgba(197,165,90,0.10)",
      };
    }
    return {
      bg: "#FAF9F6",
      bgAlt: "#ffffff",
      bgInverse: "#0a1628",
      text: "#1B1B1B",
      textOnInverse: "#FAF9F6",
      textMuted: "rgba(27,27,27,0.62)",
      textMutedOnInverse: "rgba(250,249,246,0.65)",
      gold: "#C5A55A",
      red: "#B22234",
      blue: "#002868",
      navy: "#002868",
      cardBg: "#ffffff",
      cardBorder: "rgba(27,27,27,0.08)",
      cardBgOnInverse: "#0d1e35",
      cardBorderOnInverse: "rgba(250,249,246,0.10)",
      divider: "rgba(27,27,27,0.08)",
      chipBg: "rgba(197,165,90,0.10)",
    };
  }, [isDark]);
}

// ─────────────────────────────────────────────────────────────────────────────
// Hero slides — every claim links to the page that proves it. The hook fetches
// /data/international_stats.json and /data/rankings.json at mount and builds
// three slides: (1) a country picker showing live intl-vs-US comparisons with
// a flag row to swap countries, (2) the top-markup hospital, (3) the federal
// compliance gap. The seed slide renders during first paint and on fetch
// failure so the page never shows a blank hero.
// ─────────────────────────────────────────────────────────────────────────────
type CountryRow = {
  code: string;
  name: string;
  flag: string;
  healthcare: string;
  intlCost: number;
  usCost: number;
  multiplier: number;
  scenarioCount: number;
  slug: string;
};

type SimpleSlide = {
  kind: "simple";
  flag?: string;
  eyebrow: string;
  big: string;
  body: string;
  ctaLabel: string;
  ctaTo: string;
  source: string;
  /** Optional i18n key (under homeBeta.heroSlides.simple.<key>.*) — when set,
   *  HeroSlideContent translates eyebrow/big/body/ctaLabel/source via t().
   *  Falls back to the literal strings above when undefined. */
  i18nKey?: string;
};

type CountryPickerSlide = {
  kind: "country_picker";
  countries: CountryRow[];     // ordered list to render as flag row (curated subset)
  defaultCode: string;          // which country to show on first paint
  // Real count of countries in the international crosswalk dataset (NOT just
  // the picker subset). The picker shows ~10 curated flags so the row doesn't
  // become a wall of flags, but the full crosswalk maps far more countries.
  totalCountriesIndexed: number;
};

type TopHospitalRow = {
  hospitalId: string;
  name: string;
  city: string | null;
  state: string | null;
  overallMarkup: number;
  cptMarkup: number | null;
  hcpcsMarkup: number | null;
  drgMarkup: number | null;
  revMarkup: number | null;
  // X/Y "ranked / total listed" counts shown beneath each markup cell.
  overallRanked: number;
  overallListed: number;
  cptRanked: number;
  cptListed: number;
  hcpcsRanked: number;
  hcpcsListed: number;
  drgRanked: number;
  drgListed: number;
  revRanked: number;
  revListed: number;
  outlierRate: number | null;   // % of procedures priced ≥50% above national avg
};

type NationalIndexSlide = {
  kind: "national_index";
  generatedAt: string;            // ISO date from rankings.json
  rankedCount: number;             // hospitals that meet ranking thresholds
  indexedCount: number;            // total chargemasters indexed (incl. ones excluded from rankings)
  topHospitals: TopHospitalRow[];  // top 5 by overall markup
};

// Total chargemasters we've ingested — including those excluded from rankings
// (long-term acute care, psych-only, rehab-only, hospitals with chargemaster
// data errors, hospitals with <50 comparable procedures, etc.). Matches the
// canonical figure surfaced on /hospital-rankings ("X ranked of 9,500+ indexed").
const TOTAL_INDEXED_HOSPITALS = 9582;

// ScenariosShowcaseSlide is rendered with inline scenario teasers pulled
// from the page-level featured set. The slide itself carries no data — the
// teasers come from useScenarioRows().featured so they stay in sync with
// the scenario section below the hero.
type ScenariosShowcaseSlide = {
  kind: "scenarios_showcase";
};

type Slide = SimpleSlide | CountryPickerSlide | NationalIndexSlide | ScenariosShowcaseSlide;

// Empty seed list so the hero stays clean during the brief data-loading window.
// The previous "Real numbers, every page" placeholder felt overkill; the slide
// system now renders nothing until live data arrives (sub-second on cached visits).
const SEED_SLIDES: Slide[] = [];

// Country picker order — well-known peer economies first, then breadth across
// continents. Any country not in this list (or missing data) is omitted from
// the row. Cap visually around 12 to keep the flag row to two rows on desktop.
const COUNTRY_PICKER_ORDER = [
  "DE", "FR", "GB", "IT", "NL",
  "CA", "AU", "JP", "KR", "IN",
];
const DEFAULT_COUNTRY = "DE";

type IntlCountryMeta = {
  country_code: string;
  country_name: string;
  flag_emoji: string;
  healthcare_system: string;
};
type IntlOverallRow = {
  country_code: string;
  scenario_count: number;
  avg_intl_cost: number;
  avg_us_national_cost: number;
  us_vs_intl_multiplier: number;
};
type IntlStats = {
  generated_at?: string;
  countries?: IntlCountryMeta[];
  overallComparison?: IntlOverallRow[];
};
type RankingHospital = {
  hospital_id: string;
  hospital_name: string;
  hospital_city: string | null;
  hospital_state: string | null;
  avg_gross_markup_percentage: number | null;
  // Median-baseline markup — the metric /hospital-rankings order is built on.
  median_gross_markup_percentage?: number | null;
  total_procedures: number | null;
  total_available_procedures?: number | null;
  // Per-code-type fields used by the national-index slide. *_procedure_count
  // is "procedures ranked" (passed our 3+ hospital, 50+ comparable code,
  // bucketed-unit filters). *_total_available is the total codes the
  // hospital published for that type. Together they give the "X/Y ranked"
  // X/Y display you see on /hospital-rankings.
  cpt_procedure_count?: number | null;
  hcpcs_procedure_count?: number | null;
  drg_procedure_count?: number | null;
  rev_procedure_count?: number | null;
  cpt_total_available?: number | null;
  hcpcs_total_available?: number | null;
  drg_total_available?: number | null;
  rev_total_available?: number | null;
  cpt_avg_markup_percentage?: number | null;
  hcpcs_avg_markup_percentage?: number | null;
  drg_avg_markup_percentage?: number | null;
  rev_avg_markup_percentage?: number | null;
  // Share of this hospital's billed procedures priced ≥50% above the
  // national average for that code. Used in the Outlier Rate column.
  outlier_percentage?: number | null;
};

type RankingsTop = {
  generated_at?: string;
  data?: RankingHospital[];
};

// Top 5 worst-markup hospitals with their per-code-type breakdown. This is
// what readers actually want to see — real hospitals, named, with the CPT /
// HCPCS / DRG / REV markups all in one row so you can scan where each
// chargemaster is most extreme.
function computeTopHospitals(rows: RankingHospital[]): TopHospitalRow[] {
  const numOr0 = (n: number | null | undefined) => (Number.isFinite(n) ? (n as number) : 0);
  const markupOrNull = (n: number | null | undefined) => (Number.isFinite(n) ? (n as number) : null);
  // Order + headline metric = median-baseline markup, the same basis as
  // /hospital-rankings, so the homepage top-5 IS the rankings top-5.
  const rankMetric = (r: RankingHospital) =>
    r.median_gross_markup_percentage ?? r.avg_gross_markup_percentage;
  return rows
    .filter((r) => Number.isFinite(rankMetric(r)))
    .sort((a, b) => (rankMetric(b) ?? 0) - (rankMetric(a) ?? 0))
    .slice(0, 5)
    .map((r) => ({
      hospitalId: r.hospital_id,
      name: titleCaseHospital(r.hospital_name),
      city: r.hospital_city ? titleCase(r.hospital_city) : null,
      state: r.hospital_state,
      overallMarkup: rankMetric(r) ?? 0,
      cptMarkup: markupOrNull(r.cpt_avg_markup_percentage),
      hcpcsMarkup: markupOrNull(r.hcpcs_avg_markup_percentage),
      drgMarkup: markupOrNull(r.drg_avg_markup_percentage),
      revMarkup: markupOrNull(r.rev_avg_markup_percentage),
      overallRanked: numOr0(r.total_procedures),
      overallListed: numOr0(r.total_available_procedures),
      cptRanked: numOr0(r.cpt_procedure_count),
      cptListed: numOr0(r.cpt_total_available),
      hcpcsRanked: numOr0(r.hcpcs_procedure_count),
      hcpcsListed: numOr0(r.hcpcs_total_available),
      drgRanked: numOr0(r.drg_procedure_count),
      drgListed: numOr0(r.drg_total_available),
      revRanked: numOr0(r.rev_procedure_count),
      revListed: numOr0(r.rev_total_available),
      outlierRate: markupOrNull(r.outlier_percentage),
    }));
}

// City names: plain per-word capitalization ("EL PASO" → "El Paso",
// "LA JOLLA" → "La Jolla"). Hospital names go through titleCaseHospital,
// which knows acronyms and joining words.
function titleCase(s: string): string {
  return s
    .split(/\s+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : w))
    .join(" ");
}

function useHeroSlides(): { slides: Slide[]; loaded: boolean } {
  const [slides, setSlides] = useState<Slide[]>(SEED_SLIDES);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      // Homepage slide 4 only needs countries + overallComparison — load the 13KB
      // overview, not the 46MB international_stats.json (scenarioCosts/procedurePrices
      // it never uses). The full file is loaded lazily only when a scenario's
      // country picker is opened.
      loadStaticData<IntlStats>("international_overview.json").catch(() => null),
      loadStaticData<RankingsTop>("rankings.json").catch(() => null),
    ]).then(([intlRaw, rankRaw]) => {
      if (cancelled) return;
      const out: Slide[] = [];

      // ── Slide 1: Top 5 worst markups, by chargemaster code ──────────
      // The 5 hospitals with the highest overall markup, broken out by
      // chargemaster code system (CPT / HCPCS / DRG / REV) so readers can
      // see which code family each hospital is most extreme on. Stamped
      // with rankings.json's generated_at so readers see data freshness.
      const rank = rankRaw as RankingsTop | null;
      if (rank?.data?.length) {
        const tops = computeTopHospitals(rank.data);
        if (tops.length) {
          out.push({
            kind: "national_index",
            generatedAt: rank.generated_at || new Date().toISOString(),
            rankedCount: rank.data.length,
            indexedCount: TOTAL_INDEXED_HOSPITALS,
            topHospitals: tops,
          });
        }
      }

      // ── Slide 2: Scenarios showcase ──────────────────────────────────
      // Community-built catalog pitch. The actual scenario cards are
      // rendered by HeroSlideContent from the page-level featured set so
      // the slide stays visually consistent with the scenario section below.
      out.push({ kind: "scenarios_showcase" });

      // ── Slide 3: Public data layer (API + MCP + CDN) ─────────────────
      // Differentiator no other consumer site has: AHP isn't just a search
      // surface, it's a public-utility data layer. Numbers below are hard
      // counts verified against the live DB on 2026-05-13:
      //   9,539 hospitals indexed (SELECT COUNT(*) FROM hospitals)
      //   9,186 with ingested chargemaster files
      //   562M chargemaster_mappings rows (raw pricing lines)
      //   14.5k CPT + 9.1k HCPCS + 517 REV + 783 DRG + 106k drug (NDC) + 441k
      //     supply — distinct codes with a national average (re-verified 2026-07-14).
      //     Prior 84k/64k/19k/3.6M/870k were *_pricing_averages ROW counts, not
      //     codes — do NOT relabel row counts as "codes" in public copy.
      //   YoY history 2020 → 2026 (chargemaster_files.year distribution)
      out.push({
        kind: "simple",
        i18nKey: "api",
        eyebrow: "Public utility · open data, open methodology",
        big: "The National Public Atlas for Hospital Pricing.",
        body:
          "9,500+ US hospitals indexed. 555+ million chargemaster pricing lines. 14k+ CPT, 9k+ HCPCS, 500+ revenue, 100k+ drug, 400k+ supply, and 783 MS-DRG national averages — broken down by inpatient / outpatient setting, with year-over-year history from 2020 onward as hospitals re-publish. Mirrored to a CDN, replicated on-chain, queryable via REST and MCP. Free for journalism, research, and consumer tools.",
        ctaLabel: "Browse the data layer",
        ctaTo: "/api",
        source: "americanhospitalprices.com/api/v1 \u00b7 mcp.americanhospitalprices.com",
      });

      // ── Slide 4: International country picker ────────────────────────
      const intl = intlRaw as IntlStats | null;
      if (intl?.overallComparison && intl?.countries) {
        const countryMap = new Map(intl.countries.map((c) => [c.country_code, c]));
        const overallMap = new Map(intl.overallComparison.map((r) => [r.country_code, r]));
        const countries: CountryRow[] = [];
        for (const code of COUNTRY_PICKER_ORDER) {
          const meta = countryMap.get(code);
          const row = overallMap.get(code);
          const slug = COUNTRY_SLUGS[code];
          if (!meta || !row || !slug) continue;
          if (!Number.isFinite(row.us_vs_intl_multiplier) || row.us_vs_intl_multiplier < 1.5) continue;
          countries.push({
            code,
            name: meta.country_name,
            flag: meta.flag_emoji,
            healthcare: meta.healthcare_system,
            intlCost: Math.round(row.avg_intl_cost),
            usCost: Math.round(row.avg_us_national_cost),
            multiplier: row.us_vs_intl_multiplier,
            scenarioCount: row.scenario_count,
            slug,
          });
        }
        if (countries.length) {
          // Total country count from the full crosswalk dataset, not the
          // curated picker subset. overallComparison has one row per country
          // with US comparison data, which is the honest "countries we index"
          // figure (currently ~32, up from the 10 we show as flags).
          const totalCountriesIndexed = intl.overallComparison.length;
          out.push({
            kind: "country_picker",
            countries,
            totalCountriesIndexed,
            defaultCode: countries.some((c) => c.code === DEFAULT_COUNTRY) ? DEFAULT_COUNTRY : countries[0].code,
          });
        }
      }

      // (Top-markup callout slide removed — singling out one hospital by name
      // in a 1133%-style headline reads as "we are here to shame this one
      // place" rather than "here is the systemic pattern." The National
      // Index slide above already shows the top 5 worst-markup hospitals
      // in context with their per-code-type breakdown.)

      // (Compliance-gap slide removed — redundant with the national-index
      // slide that opens the rotation showing the top 5 worst markups.)

      if (out.length) {
        setSlides(out);
        setLoaded(true);
      }
    });
    return () => { cancelled = true; };
  }, []);

  return { slides, loaded };
}

// Dignity's hero speech bubble — drawn from the same voice as the homepage
// DIGNITY_LINES + /about/dignity narration. Keep punchy: this is the consumer
// promise, not the manifesto.
const DIGNITY_INTRO = {
  name: "Dignity",
  title: "America's healthcare advocate",
  quote: "Hospital prices are public. They just hope you never look. I'll walk you through everything.",
};

// Scenario row data — loaded once from /data/scenarios_index.json (~15MB,
// cached for the session and IndexedDB across visits). Indexes 11,000+
// scenarios with phaseBreakdown + tags, which is everything ScenarioCard
// needs in compact mode. From there we group by SCENARIO_CATEGORIES (same
// taxonomy as the consumer homepage) and build a family-sibling map so the
// bottom severity bar on each card lights up correctly.
type FamilySibling = {
  id: string;
  severity_level: number | null;
  name: string;
  estimatedCost?: number | null;
  // Wired through so the family-explorer modal (and any consumer that wants
  // to surface variant complexity) can render real per-variant procedure
  // counts instead of falling back to "0 procedures". Sourced from the same
  // scenarios_index.json row that produced estimatedCost.
  totalProcedures?: number | null;
};

interface ScenarioIndexEntry {
  id: string;
  uuid: string;
  name: string;
  iconName?: string;
  color?: string;
  description?: string;
  duration?: string;
  totalProcedures?: number;
  estimatedCost?: number;
  /** Median-anchored scenario total — emitted by generate_static_cache.py
   *  alongside estimatedCost. Null on older JSON predating median rollout. */
  estimatedCostMedian?: number | null;
  phaseBreakdown?: Record<string, number>;
  /** Per-phase median totals (same shape as phaseBreakdown). Null until the
   *  cache refresh that produced this index has median data populated. */
  phaseBreakdownMedian?: Record<string, number> | null;
  phases?: string[];
  tags?: string[];
  severityLevel?: number | null;
  category?: string;
  scenarioFamilyId?: string;
  createdBy?: string | null;
  isVerified?: boolean;
  hidden?: boolean;
}

function indexEntryToCardData(s: ScenarioIndexEntry): ScenarioCardData {
  return {
    id: s.id,
    name: s.name,
    description: s.description || "",
    icon_name: s.iconName || "Activity",
    color: s.color || "#B22234",
    duration: s.duration || "",
    total_procedures: s.totalProcedures || 0,
    phases: Array.isArray(s.phases) ? s.phases : Object.keys(s.phaseBreakdown || {}),
    severity_level: s.severityLevel ?? null,
    calculated_avg_cost: s.estimatedCost || 0,
    // Pass-through median fields from the index. ScenarioCard's avg/median
    // toggle gates on `calculated_median_cost != null && != calculated_avg_cost`,
    // so cards from scenarios where the cache regenerated with median data
    // automatically render the toggle alongside the cost — no extra wiring
    // needed at the card site. Cards from scenarios where the cache still
    // lacks a median value fall back to avg-only display.
    calculated_median_cost: s.estimatedCostMedian ?? undefined,
    phaseBreakdown: s.phaseBreakdown || {},
    phaseBreakdownMedian: s.phaseBreakdownMedian ?? null,
    phaseProcedures: {}, // not in slim index — card hover-detail lazy-loads if needed
    tags: s.tags || [],
    created_by: s.createdBy || null,
    is_verified: !!s.isVerified,
  };
}

interface ScenarioRowData {
  featured: ScenarioCardData[];
  byCategory: Record<string, ScenarioCardData[]>;
  /** Counts per category for pill badges. */
  categoryCounts: Record<string, number>;
  siblingMap: Map<string, FamilySibling[]>;
  /** Direct UUID lookup — lets sections like the demo-family stack resolve a
   *  specific base scenario without re-walking byCategory. */
  byUuid: Map<string, ScenarioCardData>;
  total: number;
}

function useScenarioRows(): { data: ScenarioRowData | null; loading: boolean } {
  const [data, setData] = useState<ScenarioRowData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Use the translated variant so scenarios_index_es.json (etc.) ships
        // when the user picks a non-English locale — keeps scenario card titles
        // + descriptions consistent with the rest of the page's t() copy.
        const json = await loadTranslatedStaticData<{ data: ScenarioIndexEntry[] }>("scenarios_index.json");
        if (cancelled || !json?.data) {
          setLoading(false);
          return;
        }
        const visible = json.data.filter((s) => !s.hidden);

        // Build family → all-members map first (one pass), then derive per-scenario
        // siblings = members minus self.
        const familyToMembers = new Map<string, FamilySibling[]>();
        for (const s of visible) {
          if (!s.scenarioFamilyId) continue;
          if (!familyToMembers.has(s.scenarioFamilyId)) familyToMembers.set(s.scenarioFamilyId, []);
          familyToMembers.get(s.scenarioFamilyId)!.push({
            id: s.id,
            severity_level: s.severityLevel ?? null,
            name: s.name,
            estimatedCost: s.estimatedCost ?? null,
            totalProcedures: s.totalProcedures ?? null,
          });
        }
        const siblingMap = new Map<string, FamilySibling[]>();
        for (const s of visible) {
          if (!s.scenarioFamilyId) continue;
          const members = familyToMembers.get(s.scenarioFamilyId) ?? [];
          siblingMap.set(s.id, members.filter((m) => m.id !== s.id));
        }

        // Group by SCENARIO_CATEGORIES — exactly the same taxonomy the main
        // homepage uses, so the rows match what consumers see elsewhere.
        const byCategory: Record<string, ScenarioCardData[]> = {};
        const categoryCounts: Record<string, number> = {};
        for (const cat of SCENARIO_CATEGORIES) {
          if (cat.key === "all") continue;
          const matched = visible
            .filter((s) => scenarioMatchesCategory(s.tags || [], cat))
            // Prefer severity-0 anchors first (cleanest names + headline price),
            // then walk up the ladder so the row shows variant depth.
            .sort((a, b) => (a.severityLevel ?? 99) - (b.severityLevel ?? 99));
          byCategory[cat.key] = matched.map(indexEntryToCardData);
          categoryCounts[cat.key] = matched.length;
        }

        // Featured set — the curated UUIDs from FEATURED_SCENARIO_UUIDS, in
        // the order they're listed. Falls back gracefully if any UUID is
        // missing from the index.
        const featured: ScenarioCardData[] = [];
        const byUuid = new Map(visible.map((s) => [s.id, s]));
        for (const uuid of FEATURED_SCENARIO_UUIDS) {
          const entry = byUuid.get(uuid);
          if (entry) featured.push(indexEntryToCardData(entry));
        }

        // Card-data lookup by UUID — needed by the demo-family stack section
        // (and any future section that wants to resolve a specific base scenario).
        const cardByUuid = new Map<string, ScenarioCardData>();
        for (const s of visible) cardByUuid.set(s.id, indexEntryToCardData(s));

        if (!cancelled) {
          setData({ featured, byCategory, categoryCounts, siblingMap, byUuid: cardByUuid, total: visible.length });
          setLoading(false);
        }
      } catch {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return { data, loading };
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
export default function HomeBeta() {
  const T = useThemeTokens();
  const reviewFlags = useAppReviewFlags();
  const brand = useBrand();
  // Pull from both namespaces — 'homepage' carries the reusable hero/problem/
  // howItWorks/scenarios keys + the new homeBeta.* subtree minted for the
  // press-first proposal; 'common' covers nav, actions, error strings.
  const { t } = useTranslation(['homepage', 'common']);
  const { isDark } = useTheme();
  const { slides, loaded: slidesLoaded } = useHeroSlides();
  const { data: rowData, loading: rowsLoading } = useScenarioRows();
  const viewportTier = useViewportTier();
  // 'featured' or a SCENARIO_CATEGORIES key (excluding 'all').
  const [activeScenarioCategory, setActiveScenarioCategory] = useState<string>("featured");
  const [slideIdx, setSlideIdx] = useState(0);
  const [shareOpen, setShareOpen] = useState(false);
  const [narrationOpen, setNarrationOpen] = useState(false);
  // Procedure drilldown modal — fired from the national-index table. Same
  // shape as on /hospital-rankings: { hospitalId, hospitalName, procedureType
  // ('cpt'|'hcpcs'|'drg'|'revenue'), avgMarkup }.
  const [drilldown, setDrilldown] = useState<{
    isOpen: boolean;
    hospitalId: string | null;
    hospitalName: string;
    procedureType: string | null;
    avgMarkup: number;
  }>({ isOpen: false, hospitalId: null, hospitalName: "", procedureType: null, avgMarkup: 0 });
  const openDrilldown = (hospitalId: string, hospitalName: string, procedureType: string, avgMarkup: number) => {
    setDrilldown({ isOpen: true, hospitalId, hospitalName, procedureType, avgMarkup });
  };
  const closeDrilldown = () => setDrilldown((d) => ({ ...d, isOpen: false }));

  // Bookmark state + handler — matches the pattern in ConsumerHomepage so a
  // user who's already starred a scenario sees the star on the beta homepage
  // (same localStorage key).
  const [bookmarks, setBookmarks] = useState<Set<string>>(() => new Set(scenarioStorage.getBookmarks()));
  const handleToggleBookmark = (id: string) => {
    scenarioStorage.toggleBookmark(id);
    setBookmarks(new Set(scenarioStorage.getBookmarks()));
  };

  // CardTheme — built once from the theme tokens. ScenarioCard memos on this
  // ref so we wrap it in useMemo to avoid re-rendering all 8 cards on every
  // hero-finding tick.
  const cardTheme: CardTheme = useMemo(() => ({
    cardBg: T.cardBg,
    border: T.cardBorder,
    // Layered shadow values consumed inside ScenarioCard:
    //   shadow            → both ambient + direct shadow layers
    //   shadowTopHighlight → 1px inset top edge that communicates "raised"
    // In dark mode, a single near-black shadow against the dark page bg reads
    // as a halo/glow, so we go subtler on the dark shadow and lean on the
    // inset top highlight (slightly translucent white) for the lift cue. In
    // light mode the existing blue-tinted ambient shadow is enough; the
    // highlight is a faint white that disappears against the white card bg.
    shadow: isDark ? "rgba(0,0,0,0.55)" : "rgba(0,40,104,0.08)",
    shadowTopHighlight: isDark ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.6)",
    red: T.red,
    gold: T.gold,
    green: "#2b5e49",
    blue: T.blue,
    text: T.text,
    textMuted: T.textMuted,
  }), [T, isDark]);

  // Country selected in the picker — persists across slide rotations so a
  // user who picked "France" sees France whenever the picker slide comes back.
  const [selectedCountryCode, setSelectedCountryCode] = useState<string>(DEFAULT_COUNTRY);

  // Reset rotation to slide 0 (the country picker, when data is loaded) so
  // first impression is the strongest finding.
  useEffect(() => {
    if (slidesLoaded) {
      setSlideIdx(0);
      // Sync selected country to the picker slide's default if present.
      const picker = slides.find((s): s is CountryPickerSlide => s.kind === "country_picker");
      if (picker) setSelectedCountryCode(picker.defaultCode);
    }
  }, [slidesLoaded, slides]);

  const safeIdx = Math.min(Math.max(slideIdx, 0), Math.max(slides.length - 1, 0));
  const slide: Slide | undefined = slides[safeIdx] ?? slides[0];

  // Picker slide: derive the active country row for rendering.
  // Guarded against the brief initial state where SEED_SLIDES is empty and
  // the live data hasn't arrived yet — slide is undefined for ~one frame.
  const pickerCountries: CountryRow[] =
    slide && slide.kind === "country_picker" ? slide.countries : [];
  const activeCountry: CountryRow | undefined =
    slide && slide.kind === "country_picker"
      ? slide.countries.find((c) => c.code === selectedCountryCode) ?? slide.countries[0]
      : undefined;

  // Cycle slides every 11s — slower than before because each slide now carries
  // more weight (the picker slide especially deserves time to be explored).
  // Pauses on hero hover.
  const cycleRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused || slides.length <= 1) return;
    cycleRef.current = setInterval(() => {
      setSlideIdx((i) => (i + 1) % slides.length);
    }, 11000);
    return () => { if (cycleRef.current) clearInterval(cycleRef.current); };
  }, [paused, slides.length]);

  return (
    <div style={{ background: T.bg, color: T.text, fontFamily: sans }}>
      <Helmet>
        {/* Brand-aware: HomeBeta is the / route for ALL brands — hardcoding AHP
            here made Luigi's homepage canonicalize to AHP's domain. */}
        <title>{brand.seo.title}</title>
        <meta name="description" content="Compare real hospital prices across 9,500+ hospitals (4,800+ ranked A-F). 555M+ pricing records. 11,900+ medical scenarios. Search by procedure, ZIP code, or hospital name." />
        <link rel="canonical" href={`${brand.primaryDomain}/`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${brand.primaryDomain}/`} />
        <meta property="og:title" content={brand.seo.title} />
        <meta property="og:description" content="Compare real hospital prices across 9,500+ hospitals. 555M+ pricing records from hospitals' own published chargemasters." />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <section
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        style={{ borderBottom: `1px solid ${T.divider}`, position: "relative" }}
      >
        {/* Flag video background — autoplay, muted, looped. The video is the
            consumer signal: this is an American civic project, not a SaaS
            dashboard. mixBlendMode keeps the headline readable while the
            stripes-and-stars motion carries underneath. */}
        <div aria-hidden style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
          <video
            autoPlay
            loop
            muted
            playsInline
            poster="/consumer/hero-flag-light.webp"
            className="absolute inset-0 w-full h-full"
            style={{
              objectFit: "cover",
              opacity: isDark ? 0.28 : 0.18,
              mixBlendMode: isDark ? "screen" : "multiply",
            }}
          >
            <source src="/consumer/hero-flag-faded.mp4" type="video/mp4" />
          </video>
          {/* Gradient veil — pulls the bottom of the hero back to readable bg
              so the dot-cycler + CTAs land on a calm color, not on the flag. */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: isDark
                ? "linear-gradient(180deg, rgba(10,22,40,0.25) 0%, rgba(10,22,40,0.85) 75%, rgba(10,22,40,0.95) 100%)"
                : "linear-gradient(180deg, rgba(250,249,246,0.15) 0%, rgba(250,249,246,0.7) 70%, rgba(250,249,246,0.95) 100%)",
            }}
          />
        </div>

        {/* ── 1) Top hero: headline + search pill (full-width, centered) ───
            Primary intent handle. Dignity is NOT here — moved into the
            slide-header row below so the chargemaster table can use the
            full container width on the national-index slide. */}
        <div
          className="mx-auto px-6 relative text-center"
          style={{ maxWidth: 1100, paddingTop: 96, paddingBottom: 32, zIndex: 1 }}
        >
          <p
            style={{
              fontFamily: sans,
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "2px",
              textTransform: "uppercase",
              color: T.gold,
              marginBottom: 18,
            }}
          >
            {t('homepage:homeBeta.browse.eyebrow')}
          </p>
          <h1
            style={{
              fontFamily: serif,
              fontSize: "clamp(42px, 7vw, 86px)",
              letterSpacing: "-1.5px",
              fontWeight: 400,
              lineHeight: 1.05,
              color: T.text,
              marginBottom: 24,
            }}
          >
            {t('homepage:hero.title1')}{" "}
            <em style={{ fontStyle: "italic", color: T.red }}>{t('homepage:hero.titleEmphasis')}</em>
            <br />
            {t('homepage:hero.title2')}
          </h1>
          <p
            className="max-w-2xl mx-auto"
            style={{
              fontFamily: sans,
              fontSize: "clamp(16px, 2vw, 20px)",
              lineHeight: 1.6,
              color: T.textMuted,
              marginBottom: 40,
            }}
          >
            {/* Trans component lets translators move the <red> 32M emphasis
                and the {{hospitalCount}} placeholder into the correct word
                order for their language without breaking the JSX. */}
            <Trans
              i18nKey="homepage:hero.subtitle"
              values={{ hospitalCount: "9,500+" }}
              components={{ red: <span style={{ color: T.red, fontWeight: 600 }} /> }}
            />
          </p>
          <HeroSearchPill T={T} />
        </div>
      </section>

      {/* ── Slide showcase section ────────────────────────────────────────
          New section break: the flag video belongs to the search hero only.
          The slide content (national index / scenarios showcase / country
          picker) sits on a navy-tinted background — same family as the
          banner strip on the production homepage — so it reads as "data
          territory" and the flag motif doesn't strain through two screens
          of content. Gradient at the top blends from the hero's cream/dark
          bg into the navy wash. */}
      <section
        // Mobile: hidden. The slide showcase (national-index table, country
        // picker, scenarios showcase) is dense on a narrow viewport — the
        // mobile silhouette keeps Hero → Top-5 → carousel → Dignity → close,
        // shedding ~6,000px of scroll.
        className="hidden md:block"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        style={{
          background: isDark
            ? "linear-gradient(180deg, #0a1628 0%, #0a1f3a 18%, #0a1f3a 100%)"
            : "linear-gradient(180deg, #FAF9F6 0%, #eef2f9 14%, #e6ecf6 100%)",
          borderTop: `1px solid ${T.divider}`,
          borderBottom: `1px solid ${T.divider}`,
        }}
      >
        {/* 2) Slide header + Dignity (2-col grid)
            Left: the slide's eyebrow + big headline + body + source link
              (e.g. "Today's 5 highest-marked-up hospitals." for the national-index slide).
            Right: Dignity GLB with a compact label badge above the model
              and the "Listen to Dignity narrate this page" CTA below. */}
        <div
          className="mx-auto grid lg:grid-cols-[3fr_1fr] gap-10 items-start px-6 relative"
          style={{ maxWidth: 1240, paddingTop: 72, paddingBottom: 8 }}
        >
          {/* Slide header text aligns with the top of the Dignity column
              (label badge + GLB). Grid uses items-start so both columns
              start at the same Y — no offset needed on desktop. */}
          <div>
            {slide ? (
              <HeroSlideContent
                T={T}
                isDark={isDark}
                slide={slide}
                activeCountry={activeCountry}
                pickerCountries={pickerCountries}
                selectedCountryCode={selectedCountryCode}
                onPickCountry={(code) => { setSelectedCountryCode(code); setPaused(true); }}
                slideIdx={safeIdx}
                onOpenDrilldown={openDrilldown}
                featuredScenarios={rowData?.featured}
                scenarioBookmarks={bookmarks}
                scenarioCardTheme={cardTheme}
                onToggleScenarioBookmark={handleToggleBookmark}
                scenarioSiblingMap={rowData?.siblingMap}
                scenarioTotalCount={rowData?.total}
                part="header"
              />
            ) : (
              <div style={{ minHeight: 320 }} aria-hidden />
            )}
          </div>

          {/* Right: Dignity badge + GLB + narrate button */}
          <div className="hidden lg:flex flex-col items-center gap-4">
            <div
              className="flex items-center gap-2 px-3 py-1.5"
              style={{
                background: T.cardBg,
                border: `1px solid ${T.cardBorder}`,
                borderRadius: 999,
                boxShadow: `0 6px 18px ${isDark ? "rgba(0,0,0,0.35)" : "rgba(0,40,104,0.08)"}`,
              }}
            >
              <span style={{ fontFamily: serif, fontSize: "0.98rem", color: T.text, fontWeight: 600 }}>
                {t('homepage:homeBeta.dignityBadge.name', DIGNITY_INTRO.name)}
              </span>
              <span style={{ color: T.divider, fontSize: "0.8rem" }}>·</span>
              <span style={{ color: T.gold, fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 600 }}>
                {t('homepage:homeBeta.dignityBadge.title', DIGNITY_INTRO.title)}
              </span>
            </div>

            {/* Narrower Dignity column (75/25 layout) — model scaled down to
                240×288 so it fits comfortably in the 1fr column at standard
                container widths without bleeding into the slide text. */}
            <div style={{ width: 240, height: 288, position: "relative" }}>
              <HeroDignityModel />
              <div
                aria-hidden
                style={{
                  position: "absolute",
                  inset: "10% 10% auto auto",
                  width: 200,
                  height: 200,
                  borderRadius: "50%",
                  background: `radial-gradient(circle, ${T.gold}40 0%, transparent 65%)`,
                  zIndex: -1,
                  filter: "blur(24px)",
                }}
              />
            </div>

            <button
              type="button"
              onClick={() => setNarrationOpen(true)}
              className="inline-flex items-center gap-2 transition-transform hover:translate-x-0.5"
              style={{
                color: T.gold,
                background: "transparent",
                border: 0,
                padding: 0,
                fontWeight: 600,
                fontSize: "0.85rem",
                cursor: "pointer",
              }}
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  background: T.gold,
                  color: T.navy,
                }}
              >
                <Play className="w-3 h-3" style={{ marginLeft: 1 }} fill="currentColor" />
              </span>
              Listen to Dignity narrate this page
            </button>
          </div>
        </div>

        {/* ── 3) Full-width slide body ───────────────────────────────────────
            TopHospitalTable (chargemaster breakdown), scenarios carousel,
            or country picker — whichever the active slide requires — spans
            the full 1240px so columns are readable. CTAs and slide cycler
            dots also live here so they stretch under the full table. */}
        <div
          className="mx-auto px-6 relative"
          style={{ maxWidth: 1240, paddingTop: 12, paddingBottom: 88 }}
        >
          {slide && (
            <HeroSlideContent
              T={T}
              isDark={isDark}
              slide={slide}
              activeCountry={activeCountry}
              pickerCountries={pickerCountries}
              selectedCountryCode={selectedCountryCode}
              onPickCountry={(code) => { setSelectedCountryCode(code); setPaused(true); }}
              slideIdx={safeIdx}
              onOpenDrilldown={openDrilldown}
              featuredScenarios={rowData?.featured}
              scenarioBookmarks={bookmarks}
              scenarioCardTheme={cardTheme}
              onToggleScenarioBookmark={handleToggleBookmark}
              scenarioSiblingMap={rowData?.siblingMap}
              scenarioTotalCount={rowData?.total}
              part="body"
            />
          )}

          {/* Slide cycler dots */}
          <div className="flex items-center gap-2 mt-10 flex-wrap">
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Show slide ${i + 1}`}
                onClick={() => setSlideIdx(i)}
                style={{
                  width: i === safeIdx ? 28 : 8,
                  height: 4,
                  borderRadius: 2,
                  background: i === safeIdx ? T.gold : T.divider,
                  border: 0,
                  cursor: "pointer",
                  transition: "width 300ms ease, background 300ms ease",
                }}
              />
            ))}
            <span className="text-xs ml-3" style={{ color: T.textMuted }}>
              {slidesLoaded
                ? t('homepage:homeBeta.hero.liveFindings', { count: slides.length, defaultValue: '{{count}} live findings · hover to pause' })
                : t('homepage:homeBeta.hero.loadingLive', 'Loading live data…')}
            </span>
          </div>
        </div>
      </section>

      {/* ── Section 2: The Tool — real screenshot of /hospital-rankings ───
          Hidden in the native app: it embeds an <iframe> of the rankings page,
          which is a desktop pattern that's broken at phone width — and the
          Ranks tab already IS this tool. Web keeps it. */}
      {!isNativeApp() && (
      <section style={{ background: T.bgAlt, borderBottom: `1px solid ${T.divider}` }}>
        <div className="mx-auto px-6" style={{ maxWidth: 1240, paddingTop: 88, paddingBottom: 88 }}>
          <SectionHeader
            T={T}
            eyebrow={t('homepage:homeBeta.tool.eyebrow')}
            // Dynamic count — DEMO_DIMENSIONS keeps growing as new lenses
            // land, so we interpolate the live length instead of hardcoding
            // a stale number in the headline. Translators see {{count}} in the
            // key value and place it where their language needs the number.
            title={t('homepage:homeBeta.tool.title', { count: DEMO_DIMENSIONS.length })}
            sub={t('homepage:homeBeta.tool.sub')}
          />

          <RankingsDemoExplorer T={T} isDark={isDark} />

          <div className="mt-12 grid sm:grid-cols-3 gap-6">
            <FeatureLine T={T} icon={<Layers className="w-4 h-4" />} title={t('homepage:homeBeta.tool.features.drillTitle')} body={t('homepage:homeBeta.tool.features.drillBody')} />
            <FeatureLine T={T} icon={<Stethoscope className="w-4 h-4" />} title={t('homepage:homeBeta.tool.features.overlayTitle')} body={t('homepage:homeBeta.tool.features.overlayBody')} />
            <FeatureLine T={T} icon={<Globe className="w-4 h-4" />} title={t('homepage:homeBeta.tool.features.filtersTitle')} body={t('homepage:homeBeta.tool.features.filtersBody')} />
          </div>
        </div>
      </section>
      )}

      {/* ── Section 3: The Stories — severity is a STACK, not a ladder ─────
          Reframed to match how the site actually models scenarios:
            • Severity 0 = the base scenario, "what it costs if everything goes right."
            • Severities 1–4 = cost-quartile BUCKETS within the family. Each
              bucket can hold many real-world variants (different complications,
              comorbidities, settings) — they stack at the same severity level.
          The demo uses the real "Uncomplicated Vaginal Delivery" family
          (32 variants across all 5 severities) so the visual is honest, not
          curated. Each column is clickable into the family explorer modal
          pre-positioned to that level. */}
      <section className="hidden md:block" style={{ background: T.bgAlt, borderTop: `1px solid ${T.divider}`, borderBottom: `1px solid ${T.divider}` }}>
        <div className="mx-auto px-6" style={{ maxWidth: 1240, paddingTop: 88, paddingBottom: 88 }}>
          <SectionHeader
            T={T}
            eyebrow={t('homepage:homeBeta.stories.eyebrow')}
            title={t('homepage:homeBeta.stories.title')}
            sub={t('homepage:homeBeta.stories.sub')}
          />

          <FeaturedFamilyShowcase
            T={T}
            rowData={rowData}
            bookmarks={bookmarks}
            onToggleBookmark={handleToggleBookmark}
            cardTheme={cardTheme}
          />

          <div className="flex flex-wrap items-center justify-between gap-4 mt-12">
            <p style={{ color: T.textMuted, maxWidth: 640, fontSize: "0.92rem", lineHeight: 1.55 }}>
              {t('homepage:homeBeta.stories.captionEachCard')}
            </p>
            <Link
              to="/scenarios"
              className="inline-flex items-center gap-2"
              style={{
                background: T.gold,
                color: "#1B1B1B",
                padding: "12px 22px",
                borderRadius: 4,
                fontWeight: 700,
                fontSize: "0.9rem",
                textDecoration: "none",
              }}
            >
              {t('homepage:homeBeta.stories.browseAll')} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Section 3.5: Find your scenario — pill filter + single row.
          Mirrors the /map/:state/:hospital scenario browser: a curated
          "Featured" set as the default, with category pills (icon + label +
          count) to swap the row. One row, full bleed, embla carousel within.
          ScenarioCard receives familySiblings so the bottom severity bar
          lights up with the variant ladder per card.
          `id="scenarios-browse"` is the scroll target the scenarios-showcase
          slide's "Browse more" CTA anchors to. */}
      <section id="scenarios-browse" style={{ background: T.bgAlt, borderBottom: `1px solid ${T.divider}`, paddingTop: 88, paddingBottom: 88, scrollMarginTop: 24 }}>
        <div className="mx-auto px-6 sm:px-12" style={{ maxWidth: 1240, marginBottom: 20 }}>
          <SectionHeader
            T={T}
            eyebrow={t('homepage:homeBeta.browse.eyebrow')}
            title={t('homepage:homeBeta.browse.title', { count: Math.floor((rowData?.total ?? 12000) / 1000) })}
            sub={t('homepage:homeBeta.browse.sub', { total: (rowData?.total ?? 12000).toLocaleString() })}
          />
        </div>

        {/* Pill filter bar */}
        {rowData && (
          <div
            className="mx-auto px-6 sm:px-12 flex flex-wrap gap-2 mt-2"
            style={{ maxWidth: 1240, marginBottom: 24 }}
          >
            <ScenarioCategoryPill
              T={T}
              icon={<Sparkles className="w-3.5 h-3.5" />}
              label="Featured"
              count={rowData.featured.length}
              active={activeScenarioCategory === "featured"}
              accent={T.gold}
              onClick={() => setActiveScenarioCategory("featured")}
            />
            {SCENARIO_CATEGORIES.filter((c) => c.key !== "all").map((cat) => {
              const count = rowData.categoryCounts[cat.key] || 0;
              if (count < 2) return null;
              const Icon = SCENARIO_CATEGORY_ICONS[cat.key] || Activity;
              return (
                <ScenarioCategoryPill
                  key={cat.key}
                  T={T}
                  icon={<Icon className="w-3.5 h-3.5" />}
                  label={cat.label}
                  count={count}
                  active={activeScenarioCategory === cat.key}
                  accent={cat.color}
                  onClick={() => setActiveScenarioCategory(cat.key)}
                />
              );
            })}
          </div>
        )}

        {/* Active row — keyed on activeScenarioCategory so embla resets to start
            on filter change. CategoryBrowseRow handles progressive loading
            (15 cards initial, 15 more as you scroll) and chevron nav. */}
        <div style={{ width: "100%" }}>
          {rowsLoading && (
            <div className="px-6 sm:px-12" style={{ color: T.textMuted, fontSize: "0.9rem" }}>
              Loading scenarios from the live dataset…
            </div>
          )}
          {!rowsLoading && rowData && (() => {
            const isFeatured = activeScenarioCategory === "featured";
            const cat = SCENARIO_CATEGORIES.find((c) => c.key === activeScenarioCategory);
            const scenarios = isFeatured
              ? rowData.featured
              : rowData.byCategory[activeScenarioCategory] || [];
            const label = isFeatured ? "Featured scenarios" : (cat?.browseLabel ?? "Scenarios");
            const color = isFeatured ? T.gold : (cat?.color ?? T.gold);
            if (scenarios.length < 1) {
              return (
                <div className="px-6 sm:px-12" style={{ color: T.textMuted, fontSize: "0.9rem" }}>
                  No scenarios in this category yet.
                </div>
              );
            }
            return (
              <CategoryBrowseRow
                key={activeScenarioCategory}
                label={label}
                color={color}
                count={scenarios.length}
                scenarios={scenarios}
                viewportTier={viewportTier}
                renderCard={(s: ScenarioCardData) => (
                  <ScenarioCard
                    key={s.id}
                    scenario={s}
                    compact
                    isStarred={bookmarks.has(s.id)}
                    theme={cardTheme}
                    onToggleBookmark={handleToggleBookmark}
                    familySiblings={rowData.siblingMap.get(s.id)}
                  />
                )}
              />
            );
          })()}
        </div>

        <div className="mx-auto px-6 sm:px-12 flex flex-wrap items-center justify-between gap-4" style={{ maxWidth: 1240, marginTop: 16 }}>
          <p style={{ color: T.textMuted, maxWidth: 620, fontSize: "0.92rem" }}>
            Each card's bottom bar shows the severity ladder — from uncomplicated (0)
            to catastrophic (4). Click any pill on the card to walk that variant.
          </p>
          <Link
            to="/scenarios"
            className="inline-flex items-center gap-2"
            style={{
              background: T.navy,
              color: "#FAF9F6",
              padding: "12px 22px",
              borderRadius: 4,
              fontWeight: 600,
              fontSize: "0.9rem",
              textDecoration: "none",
            }}
          >
            Open the scenario browser <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* ── Section 3.6: Connect to Care — empowerment flow ─────────────── */}
      <section className="hidden md:block" style={{ background: T.bg, borderBottom: `1px solid ${T.divider}` }}>
        <div className="mx-auto px-6" style={{ maxWidth: 1240, paddingTop: 96, paddingBottom: 96 }}>
          <SectionHeader
            T={T}
            eyebrow={t('homepage:homeBeta.connect.eyebrow')}
            title={t('homepage:homeBeta.connect.title')}
            sub={t('homepage:homeBeta.connect.sub')}
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            <EmpowermentStep
              T={T}
              step={1}
              icon={<Search className="w-5 h-5" />}
              title={t('homepage:homeBeta.connect.tiles.findTitle')}
              body={t('homepage:homeBeta.connect.tiles.findBody')}
            />
            <EmpowermentStep
              T={T}
              step={2}
              icon={<TrendingUp className="w-5 h-5" />}
              title={t('homepage:homeBeta.connect.tiles.costTitle')}
              body={t('homepage:homeBeta.connect.tiles.costBody')}
            />
            <EmpowermentStep
              T={T}
              step={3}
              icon={<HandHeart className="w-5 h-5" />}
              title={t('homepage:homeBeta.connect.tiles.payTitle')}
              body={t('homepage:homeBeta.connect.tiles.payBody')}
            />
          </div>

          {/* Connect-to-Care category strip — mirrors the four categories on
              ScenarioCareResources so consumers know what to expect. */}
          <div
            className="mt-12 p-6"
            style={{
              background: T.cardBg,
              border: `1px solid ${T.cardBorder}`,
              borderRadius: 8,
            }}
          >
            <div className="flex items-center gap-2 mb-5">
              <Sparkles className="w-4 h-4" style={{ color: T.gold }} />
              <span style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.12em", color: T.gold, fontWeight: 700 }}>
                {t('homepage:homeBeta.connect.panelEyebrow')}
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <CareCategoryChip T={T} icon={<DollarSign className="w-4 h-4" />} color="#2b5e49" label={t('homepage:homeBeta.connect.categories.financialLabel')} body={t('homepage:homeBeta.connect.categories.financialBody')} />
              <CareCategoryChip T={T} icon={<Building2 className="w-4 h-4" />} color="#002868" label={t('homepage:homeBeta.connect.categories.programsLabel')} body={t('homepage:homeBeta.connect.categories.programsBody')} />
              <CareCategoryChip T={T} icon={<Users className="w-4 h-4" />} color="#7B5EA7" label={t('homepage:homeBeta.connect.categories.peerLabel')} body={t('homepage:homeBeta.connect.categories.peerBody')} />
              <CareCategoryChip T={T} icon={<HandHeart className="w-4 h-4" />} color="#B22234" label={t('homepage:homeBeta.connect.categories.advocacyLabel')} body={t('homepage:homeBeta.connect.categories.advocacyBody')} />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 mt-6 pt-5" style={{ borderTop: `1px solid ${T.cardBorder}` }}>
              <span style={{ color: T.textMuted, fontSize: "0.85rem" }}>
                {t('homepage:homeBeta.connect.filterNote')}
              </span>
              <Link
                to="/scenarios/1b52ae2b-d892-4602-a05f-1a55435f4785/uncomplicated-vaginal-delivery"
                className="inline-flex items-center gap-2"
                style={{
                  background: T.gold,
                  color: "#1B1B1B",
                  padding: "10px 18px",
                  borderRadius: 4,
                  fontWeight: 700,
                  fontSize: "0.85rem",
                  textDecoration: "none",
                  whiteSpace: "nowrap",
                }}
              >
                {t('homepage:homeBeta.connect.sampleScenarioCta')} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Section 4: Meet Dignity — consumer-friendly intro ───────────── */}
      <section style={{ background: T.bgAlt, borderBottom: `1px solid ${T.divider}` }}>
        <div className="mx-auto px-6" style={{ maxWidth: 1240, paddingTop: 96, paddingBottom: 96 }}>
          <div className="grid lg:grid-cols-[1fr_1.4fr] gap-12 items-center">
            <div className="flex justify-center">
              <div style={{ width: 280, height: 340, position: "relative" }}>
                <HeroDignityModel />
                <div
                  aria-hidden
                  style={{
                    position: "absolute",
                    inset: "8% 8% auto auto",
                    width: 220,
                    height: 220,
                    borderRadius: "50%",
                    background: `radial-gradient(circle, ${T.red}30 0%, transparent 65%)`,
                    zIndex: -1,
                    filter: "blur(28px)",
                  }}
                />
              </div>
            </div>

            <div>
              <div
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] mb-5"
                style={{ color: T.gold, fontWeight: 600 }}
              >
                <Sparkles className="w-3.5 h-3.5" /> {t('homepage:homeBeta.dignityIntro.eyebrow')}
              </div>
              <h2 style={{ fontFamily: serif, fontSize: "clamp(2rem, 4.2vw, 3.4rem)", lineHeight: 1.05, letterSpacing: "-0.02em", color: T.text, margin: 0 }}>
                {t('homepage:homeBeta.dignityIntro.title')}
              </h2>
              <p style={{ color: T.textMuted, fontSize: "1.05rem", lineHeight: 1.6, marginTop: 22, maxWidth: 580 }}>
                {t('homepage:homeBeta.dignityIntro.body1')}
              </p>
              <p style={{ color: T.textMuted, fontSize: "1.05rem", lineHeight: 1.6, marginTop: 16, maxWidth: 580 }}>
                {t('homepage:homeBeta.dignityIntro.body2')}
              </p>

              <div className="flex flex-wrap items-center gap-4 mt-8">
                <Link
                  to="/about/dignity"
                  className="inline-flex items-center gap-2"
                  style={{
                    background: T.navy,
                    color: "#FAF9F6",
                    padding: "12px 22px",
                    borderRadius: 4,
                    fontWeight: 600,
                    fontSize: "0.92rem",
                    textDecoration: "none",
                  }}
                >
                  <MessageCircle className="w-4 h-4" /> {t('homepage:homeBeta.dignityIntro.ctaMeet')}
                </Link>
                <button
                  type="button"
                  onClick={() => setNarrationOpen(true)}
                  className="inline-flex items-center gap-2"
                  style={{
                    color: T.gold,
                    background: "transparent",
                    border: `1px solid ${T.gold}`,
                    padding: "11px 20px",
                    borderRadius: 4,
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    cursor: "pointer",
                  }}
                >
                  <Play className="w-3.5 h-3.5" fill="currentColor" /> {t('homepage:homeBeta.dignityIntro.ctaListen')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Section 5: The Atlas — four sub-findings ────────────────────── */}
      <section className="hidden md:block" style={{ background: T.bgAlt, borderBottom: `1px solid ${T.divider}` }}>
        <div className="mx-auto px-6" style={{ maxWidth: 1240, paddingTop: 88, paddingBottom: 88 }}>
          <SectionHeader
            T={T}
            eyebrow={t('homepage:homeBeta.atlas.eyebrow')}
            title={t('homepage:homeBeta.atlas.title')}
            sub={t('homepage:homeBeta.atlas.sub')}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10 items-start">
            {/* Card 1: redesigned — multiplier ladder driven by /international data */}
            <MultipliersAtlasCard T={T} />
            {/* Card 2: redesigned — paginated top-25 worst single-procedure markups */}
            <WorstMarkupsAtlasCard T={T} />
            {/* Card 3: redesigned — /data-sources compliance tracker thumb moved
             *  here from Card 4 since the visual IS the "required to publish,
             *  designed to obscure" evidence. */}
            <ObscureAtlasCard T={T} />
            {/* Card 4: methodology + recent release notes (single-column now) */}
            <MethodologyAtlasCard T={T} />
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mt-8">
            <SmallTile T={T} icon={<TrendingUp className="w-4 h-4" />} title={t('homepage:homeBeta.atlas.extra.watchdogTitle')} body={t('homepage:homeBeta.atlas.extra.watchdogBody')} to="/watchdog" />
            {/* Stories tile hidden until the community section is seeded (show_stories flag) */}
            {reviewFlags.showStories && (
              <SmallTile T={T} icon={<Heart className="w-4 h-4" />} title={t('homepage:homeBeta.atlas.extra.storiesTitle')} body={t('homepage:homeBeta.atlas.extra.storiesBody')} to="/stories" />
            )}
          </div>
        </div>
      </section>

      {/* ── Section 6: The App — honest about what ships ────────────────── */}
      <section className="hidden md:block" style={{ background: T.bg, borderBottom: `1px solid ${T.divider}` }}>
        <div className="mx-auto px-6" style={{ maxWidth: 1240, paddingTop: 88, paddingBottom: 88 }}>
          <div className="grid lg:grid-cols-[1fr_1.2fr] gap-12 items-center">
            <div>
              <div
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] mb-5"
                style={{ color: T.gold, fontWeight: 600 }}
              >
                <Monitor className="w-3.5 h-3.5" /> {t('homepage:homeBeta.desktopApp.eyebrow')}
              </div>
              <h2
                style={{
                  fontFamily: serif,
                  fontSize: "clamp(2rem, 4vw, 3.4rem)",
                  lineHeight: 1.05,
                  letterSpacing: "-0.02em",
                  color: T.text,
                  margin: 0,
                }}
              >
                {t('homepage:homeBeta.desktopApp.title')}
              </h2>
              <p style={{ color: T.textMuted, fontSize: "1.05rem", lineHeight: 1.55, marginTop: 24, maxWidth: 540 }}>
                {t('homepage:homeBeta.desktopApp.body1')}
              </p>
              <p style={{ color: T.textMuted, fontSize: "1.05rem", lineHeight: 1.55, marginTop: 16, maxWidth: 540 }}>
                {t('homepage:homeBeta.desktopApp.body2')}
              </p>

              <div className="flex flex-wrap items-center gap-4 mt-8">
                <span
                  className="inline-flex items-center gap-2"
                  style={{
                    background: T.navy,
                    color: "#FAF9F6",
                    padding: "14px 26px",
                    borderRadius: 4,
                    fontWeight: 600,
                    opacity: 0.78,
                    cursor: "default",
                  }}
                >
                  <Download className="w-4 h-4" /> {t('homepage:homeBeta.desktopApp.cta')}
                  <span
                    style={{
                      marginLeft: 8,
                      fontSize: "0.68rem",
                      letterSpacing: "0.12em",
                      background: T.gold,
                      color: "#1B1B1B",
                      padding: "2px 9px",
                      borderRadius: 999,
                      fontWeight: 700,
                    }}
                  >
                    {t('homepage:homeBeta.tool.comingSoon', { defaultValue: 'COMING SOON' })}
                  </span>
                </span>
                <span style={{ color: T.textMuted, fontSize: "0.85rem" }}>
                  {t('homepage:homeBeta.desktopApp.iphoneNote')}
                </span>
              </div>
            </div>

            <div
              style={{
                background: T.cardBg,
                border: `1px solid ${T.cardBorder}`,
                borderRadius: 8,
                padding: 28,
              }}
            >
              <TrustRow T={T} title={t('homepage:homeBeta.trust.summaryTitle')} body={t('homepage:homeBeta.trust.summaryBody')} />
              <TrustRow T={T} title={t('homepage:homeBeta.trust.deepTitle')} body={t('homepage:homeBeta.trust.deepBody')} />
              <TrustRow T={T} title={t('homepage:homeBeta.trust.privacyTitle')} body={t('homepage:homeBeta.trust.privacyBody')} />
              <TrustRow T={T} title={t('homepage:homeBeta.trust.futureTitle')} body={t('homepage:homeBeta.trust.futureBody')} last />
            </div>
          </div>
        </div>
      </section>

      {/* ── Section 7: The Close — share CTA ────────────────────────────── */}
      <section style={{ background: T.bgInverse, color: T.textOnInverse }}>
        <div className="mx-auto px-6 text-center" style={{ maxWidth: 820, paddingTop: 96, paddingBottom: 96 }}>
          <Quote className="w-8 h-8 mx-auto mb-6" style={{ color: T.gold }} />
          <h2
            style={{
              fontFamily: serif,
              fontSize: "clamp(2rem, 4.4vw, 3.6rem)",
              lineHeight: 1.05,
              letterSpacing: "-0.02em",
              color: T.textOnInverse,
              margin: 0,
            }}
          >
            {t('homepage:homeBeta.close.title')}
          </h2>
          <p style={{ color: T.textMutedOnInverse, fontSize: "1.1rem", lineHeight: 1.55, marginTop: 24 }}>
            {t('homepage:homeBeta.close.sub')}
          </p>
          <button
            type="button"
            onClick={() => setShareOpen(true)}
            className="inline-flex items-center gap-2 mt-8 transition-transform hover:scale-[1.02]"
            style={{
              background: T.gold,
              color: "#1B1B1B",
              padding: "16px 32px",
              borderRadius: 4,
              fontWeight: 700,
              fontSize: "1rem",
              border: 0,
              cursor: "pointer",
              boxShadow: "0 12px 32px rgba(197,165,90,0.35)",
            }}
          >
            <Share2 className="w-4 h-4" /> {t('homepage:homeBeta.close.shareCta')}
          </button>
          <p style={{ color: T.textMutedOnInverse, fontSize: "0.85rem", marginTop: 16 }}>
            Press contact + downloadable methodology PDF at the bottom of <Link to="/data-methodology" style={{ color: T.gold }}>/data-methodology</Link>.
          </p>
        </div>
      </section>

      {/* ── Fixed bottom-right install CTA (desktop only) ───────────────── */}
      <span
        aria-label={t('homepage:homeBeta.desktopApp.downloadAria', 'macOS app — coming soon')}
        className="hidden lg:inline-flex items-center gap-2"
        style={{
          position: "fixed",
          right: 24,
          bottom: 24,
          background: T.navy,
          color: "#FAF9F6",
          padding: "12px 18px",
          borderRadius: 999,
          fontWeight: 600,
          fontSize: "0.85rem",
          boxShadow: "0 12px 32px rgba(0,0,0,0.35), 0 0 0 1px rgba(197,165,90,0.4)",
          zIndex: 60,
          opacity: 0.9,
          cursor: "default",
        }}
      >
        <Download className="w-4 h-4" style={{ color: T.gold }} />
        <span>{t('homepage:homeBeta.desktopApp.cta')}</span>
        <span
          style={{
            fontSize: "0.62rem",
            letterSpacing: "0.1em",
            background: T.gold,
            color: "#1B1B1B",
            padding: "1px 8px",
            borderRadius: 999,
            fontWeight: 700,
          }}
        >
          {t('homepage:homeBeta.tool.comingSoon', { defaultValue: 'COMING SOON' })}
        </span>
      </span>

      {/* ── Dignity narration — overlay opens when user clicks "Listen to me".
          Page-key='home-beta' so lines are addressable per-page in the
          dignity_narration_lines table. Renders silent if no lines seeded yet
          — safe to ship before narration audio exists.

          autoStart is REQUIRED here (2026-08-02). Both CTAs are already play
          buttons, so mounting the player must open the overlay — without it the
          player rendered its own trigger card down here below the footer, off
          the top of the user's screen, and both CTAs looked broken. onClose
          clears narrationOpen so dismissing the overlay unmounts us instead of
          leaving that card behind on the page. */}
      {narrationOpen && (
        <DignityNarrationPlayer
          pageKey="home-beta"
          autoStart
          onClose={() => setNarrationOpen(false)}
        />
      )}

      <ShareDignityModal
        isOpen={shareOpen}
        onClose={() => setShareOpen(false)}
        shareUrl={typeof window !== "undefined" ? `${window.location.origin}/` : undefined}
      />

      <ProcedureDrilldownModal
        isOpen={drilldown.isOpen}
        onClose={closeDrilldown}
        hospitalId={drilldown.hospitalId}
        hospitalName={drilldown.hospitalName}
        procedureType={drilldown.procedureType}
        avgMarkup={drilldown.avgMarkup}
      />

      <style>{`
        @keyframes homebeta-fade-up {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────
// HeroSlideContent — renders the left column of the hero. Branches on slide
// kind so the flag picker only renders when the active slide is the country
// comparison. Each rendered "view" (eyebrow / big / body / source / CTA) is
// keyed on a string that changes when the slide *or* the selected country
// changes, so the fade-up animation replays on flag clicks too.
function HeroSlideContent({
  T, isDark, slide, activeCountry, pickerCountries, selectedCountryCode, onPickCountry, slideIdx, onOpenDrilldown,
  featuredScenarios, scenarioBookmarks, scenarioCardTheme, onToggleScenarioBookmark, scenarioSiblingMap,
  scenarioTotalCount, part = "all",
}: {
  T: ReturnType<typeof useThemeTokens>;
  isDark: boolean;
  slide: Slide;
  activeCountry: CountryRow | undefined;
  pickerCountries: CountryRow[];
  selectedCountryCode: string;
  onPickCountry: (code: string) => void;
  slideIdx: number;
  onOpenDrilldown: (hospitalId: string, hospitalName: string, procedureType: string, avgMarkup: number) => void;
  featuredScenarios?: ScenarioCardData[];
  scenarioBookmarks?: Set<string>;
  scenarioCardTheme?: CardTheme;
  onToggleScenarioBookmark?: (id: string) => void;
  scenarioSiblingMap?: Map<string, FamilySibling[]>;
  scenarioTotalCount?: number;
  // Render only the header text (eyebrow + headline + body + source), only the
  // body content (TopHospitalTable / scenarios carousel / country picker + CTAs),
  // or both. Used by HomeBeta to render the header next to the Dignity column
  // while the body content spans full width below.
  part?: "header" | "body" | "all";
}) {
  const { t } = useTranslation(['homepage']);
  // Derive a single "view" object so the render is uniform regardless of slide kind.
  const view = slide.kind === "country_picker" && activeCountry
    ? {
        flag: undefined,
        flagCode: activeCountry.code.toLowerCase(),
        // Lead with WHAT this surface is (positioning) rather than a single
        // currency comparison — the active country's numbers still anchor
        // the body so the picker drives meaningful detail per click. CTAs
        // below give two paths: the international dashboard for the full
        // crosswalk, and a per-country deep-dive for the currently picked
        // country.
        eyebrow: t('homepage:homeBeta.heroSlides.country.eyebrow', { count: slide.totalCountriesIndexed, scenarios: activeCountry.scenarioCount.toLocaleString() }),
        big: t('homepage:homeBeta.heroSlides.country.big'),
        body: t('homepage:homeBeta.heroSlides.country.body', { count: slide.totalCountriesIndexed, country: activeCountry.name, healthcare: activeCountry.healthcare.toLowerCase(), intlCost: activeCountry.intlCost.toLocaleString(), usCost: activeCountry.usCost.toLocaleString(), multiplier: activeCountry.multiplier.toFixed(1), pickerCount: pickerCountries.length }),
        ctaLabel: t('homepage:homeBeta.heroSlides.country.cta'),
        ctaTo: "/international",
        source: t('homepage:homeBeta.heroSlides.country.source', { slug: activeCountry.slug }),
      }
    : slide.kind === "national_index"
    ? {
        flag: undefined,
        flagCode: undefined,
        eyebrow: t('homepage:homeBeta.heroSlides.nationalIndex.eyebrow', { date: formatGeneratedDate(slide.generatedAt) }),
        big: t('homepage:homeBeta.heroSlides.nationalIndex.big'),
        body: t('homepage:homeBeta.heroSlides.nationalIndex.body', { indexedCount: slide.indexedCount.toLocaleString(), rankedCount: slide.rankedCount.toLocaleString() }),
        ctaLabel: t('homepage:homeBeta.heroSlides.nationalIndex.cta'),
        ctaTo: "/hospital-rankings",
        source: t('homepage:homeBeta.heroSlides.nationalIndex.source', { rankedCount: slide.rankedCount.toLocaleString(), indexedCount: slide.indexedCount.toLocaleString(), date: formatGeneratedDate(slide.generatedAt) }),
      }
    : slide.kind === "simple"
    ? {
        flag: slide.flag,
        flagCode: undefined,
        // Simple slides come from SEED_SLIDES (English literals). Each slide
        // has a stable `key` we use to look up i18n; falls back to the
        // English literal when no translation is defined.
        eyebrow: slide.i18nKey ? t(`homepage:homeBeta.heroSlides.simple.${slide.i18nKey}.eyebrow`, slide.eyebrow) : slide.eyebrow,
        big: slide.i18nKey ? t(`homepage:homeBeta.heroSlides.simple.${slide.i18nKey}.big`, slide.big) : slide.big,
        body: slide.i18nKey ? t(`homepage:homeBeta.heroSlides.simple.${slide.i18nKey}.body`, slide.body) : slide.body,
        ctaLabel: slide.i18nKey ? t(`homepage:homeBeta.heroSlides.simple.${slide.i18nKey}.cta`, slide.ctaLabel) : slide.ctaLabel,
        ctaTo: slide.ctaTo,
        source: slide.i18nKey ? t(`homepage:homeBeta.heroSlides.simple.${slide.i18nKey}.source`, slide.source) : slide.source,
      }
    : slide.kind === "scenarios_showcase"
    ? (() => {
        // Round down to the nearest 100 so the headline doesn't churn every
        // time someone adds a new scenario. "11,847" reads as a frozen
        // snapshot; "11,800+" reads as a living catalog.
        const total = scenarioTotalCount ?? 11000;
        const rounded = Math.floor(total / 100) * 100;
        return {
          flag: undefined,
          flagCode: undefined,
          eyebrow: t('homepage:homeBeta.heroSlides.scenariosShowcase.eyebrow'),
          big: t('homepage:homeBeta.heroSlides.scenariosShowcase.big', { count: rounded.toLocaleString() }),
          body: t('homepage:homeBeta.heroSlides.scenariosShowcase.body'),
          ctaLabel: t('homepage:homeBeta.heroSlides.scenariosShowcase.cta'),
          ctaTo: "/scenarios",
          source: t('homepage:homeBeta.heroSlides.scenariosShowcase.source'),
        };
      })()
    : null;

  if (!view) return null;

  // Animation key: re-trigger fade-up when slide *or* selected country changes.
  const animKey = slide.kind === "country_picker"
    ? `picker-${selectedCountryCode}`
    : slide.kind === "national_index"
    ? `index-${slide.generatedAt}`
    : slide.kind === "scenarios_showcase"
    ? "scenarios"
    : `simple-${slideIdx}`;

  const showHeader = part === "header" || part === "all";
  const showBody = part === "body" || part === "all";

  return (
    <>
      {showHeader && (<>
      <div
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] mb-6"
        style={{ color: T.gold, fontWeight: 600 }}
      >
        <span style={{ width: 24, height: 1, background: T.gold, display: "inline-block" }} />
        {view.flagCode && (
          <img
            src={`https://flagcdn.com/w40/${view.flagCode}.png`}
            alt=""
            width={20}
            height={14}
            style={{ borderRadius: 2, display: "inline-block", verticalAlign: "middle", boxShadow: "0 0 0 1px rgba(0,0,0,0.08)" }}
            aria-hidden
          />
        )}
        {!view.flagCode && view.flag && <span style={{ fontSize: "1rem", lineHeight: 1 }} aria-hidden>{view.flag}</span>}
        {view.eyebrow}
      </div>
      <h1
        key={`${animKey}-h1`}
        style={{
          fontFamily: serif,
          fontSize: "clamp(2.6rem, 5.8vw, 5.4rem)",
          lineHeight: 1.02,
          letterSpacing: "-0.02em",
          color: T.text,
          margin: 0,
          animation: "homebeta-fade-up 700ms cubic-bezier(.2,.7,.2,1) both",
        }}
      >
        {/* National-index slide forces a line break after "highest" so the
            compound "marked-up hospitals" stays together as one rhythm unit
            instead of wrapping unpredictably between "marked-up" and
            "hospitals" at narrow viewport widths. */}
        {slide.kind === "national_index" ? (
          <Trans
            i18nKey="homepage:homeBeta.heroSlides.nationalIndex.bigBroken"
            components={{ br: <br />, nb: <span style={{ whiteSpace: "nowrap" }} /> }}
          />
        ) : (
          view.big
        )}
      </h1>
      {/* For scenarios_showcase, render the intro paragraph next to a
          sample scenario card (side-by-side) and inline the Browse CTA.
          The full-width body row below is suppressed for this slide so the
          whole pitch lives in the header column — much less vertical real
          estate than the previous stacked layout. */}
      {slide.kind === "scenarios_showcase" && featuredScenarios && featuredScenarios.length > 0 && scenarioCardTheme ? (
        <div
          className="flex flex-col md:flex-row gap-6 items-start mt-7"
          key={`${animKey}-body`}
          style={{ animation: "homebeta-fade-up 800ms 100ms cubic-bezier(.2,.7,.2,1) both" }}
        >
          <div style={{ flex: 1, minWidth: 0, maxWidth: 520 }}>
            <p
              style={{
                fontFamily: sans,
                color: T.textMuted,
                fontSize: "1.0625rem",
                lineHeight: 1.55,
                margin: 0,
              }}
            >
              {view.body}
            </p>
            <button
              type="button"
              onClick={() => {
                const target = document.getElementById("scenarios-browse");
                if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className="inline-flex items-center gap-2 mt-5 transition-transform hover:translate-x-0.5"
              style={{
                background: T.navy,
                color: "#FAF9F6",
                padding: "12px 22px",
                borderRadius: 4,
                fontWeight: 600,
                fontSize: "0.9rem",
                letterSpacing: "0.01em",
                border: 0,
                cursor: "pointer",
                boxShadow: isDark ? "0 6px 18px rgba(0,0,0,0.4)" : "0 6px 18px rgba(0,40,104,0.18)",
              }}
            >
              {t('homepage:homeBeta.heroSlides.scenariosShowcase.cta')} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <div style={{ flex: "0 0 440px", minWidth: 440, maxWidth: 480 }}>
            <ScenarioCard
              scenario={featuredScenarios[0]}
              compact
              isStarred={(scenarioBookmarks ?? new Set()).has(featuredScenarios[0].id)}
              theme={scenarioCardTheme}
              onToggleBookmark={onToggleScenarioBookmark ?? (() => {})}
              familySiblings={scenarioSiblingMap?.get(featuredScenarios[0].id)}
            />
          </div>
        </div>
      ) : (
        <p
          key={`${animKey}-body`}
          style={{
            fontFamily: sans,
            color: T.textMuted,
            fontSize: "1.125rem",
            lineHeight: 1.55,
            marginTop: 28,
            maxWidth: 580,
            animation: "homebeta-fade-up 800ms 100ms cubic-bezier(.2,.7,.2,1) both",
          }}
        >
          {view.body}
        </p>
      )}

      <Link
        to={view.ctaTo}
        key={`${animKey}-source`}
        className="inline-flex items-center gap-1.5 mt-4 hover:opacity-100 transition-opacity"
        style={{
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: "0.72rem",
          color: T.textMuted,
          textDecoration: "none",
          opacity: 0.75,
          animation: "homebeta-fade-up 900ms 200ms cubic-bezier(.2,.7,.2,1) both",
        }}
      >
        <ShieldCheck className="w-3 h-3" style={{ color: T.gold }} />
        {view.source}
      </Link>
      </>)}

      {/* ── BODY ─────────────────────────────────────────────────────────
          Slide-specific content + CTAs. In HomeBeta this renders in a
          separate full-width row below the header+Dignity grid so the
          chargemaster table can spread the full container width. */}
      {showBody && (<>

      {/* National-index table — top 5 worst-markup hospitals with their
          per-chargemaster-code-system breakdown. Column header colors and
          tooltip copy mirror /hospital-rankings so the visual language is
          consistent across the two surfaces. */}
      {slide.kind === "national_index" && (
        <div className="mt-7">
          <div
            className="text-[10px] uppercase tracking-[0.18em] mb-3 inline-flex items-center gap-2"
            style={{ color: T.textMuted, fontWeight: 600 }}
          >
            <span style={{ width: 12, height: 1, background: T.gold, display: "inline-block" }} />
            {t('homepage:homeBeta.heroSlides.nationalIndex.tableHeader', { defaultValue: 'Chargemaster breakdown · sorted by overall markup' })}
          </div>
          <TopHospitalTable T={T} isDark={isDark} rows={slide.topHospitals} onOpenDrilldown={onOpenDrilldown} />

          {/* "Ranked of indexed" line — same phrasing as /hospital-rankings,
              with the same tooltip explaining why some hospitals aren't ranked. */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-3" style={{ fontSize: "0.78rem", color: T.textMuted, maxWidth: 720 }}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className="cursor-help"
                  style={{
                    textDecoration: "underline dotted",
                    textDecorationThickness: 1,
                    textUnderlineOffset: 2,
                  }}
                >
                  <Trans
                    i18nKey="homepage:homeBeta.heroSlides.nationalIndex.rankedOfIndexed"
                    values={{ rankedCount: slide.rankedCount.toLocaleString(), indexedCount: slide.indexedCount.toLocaleString() }}
                    components={{ b: <span style={{ fontWeight: 700, color: T.text }} /> }}
                  />
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom" style={{ maxWidth: 320, fontSize: "0.8rem", lineHeight: 1.5 }}>
                <p style={{ marginBottom: 6 }}>
                  <Trans
                    i18nKey="homepage:homeBeta.heroSlides.nationalIndex.tipRanked"
                    components={{ b: <strong /> }}
                  />
                </p>
                <p style={{ marginBottom: 6 }}>
                  <Trans
                    i18nKey="homepage:homeBeta.heroSlides.nationalIndex.tipBenchmark"
                    components={{ b: <strong /> }}
                  />
                </p>
                <p style={{ marginBottom: 0 }}>
                  {t('homepage:homeBeta.heroSlides.nationalIndex.tipExcluded')}
                </p>
              </TooltipContent>
            </Tooltip>
            <Link to="/data-sources" style={{ color: T.gold, fontWeight: 600, textDecoration: "none" }}>
              {t('homepage:homeBeta.heroSlides.nationalIndex.allHospitalsLink', { count: slide.indexedCount.toLocaleString() })}
            </Link>
            <Link to="/data-methodology" style={{ color: T.gold, fontWeight: 600, textDecoration: "none" }}>
              {t('homepage:homeBeta.heroSlides.nationalIndex.methodologyLink')}
            </Link>
          </div>
        </div>
      )}

      {/* (Scenarios showcase body content moved into the header section
          above so the sample card sits next to the intro paragraph, side-
          by-side — way less vertical space than the previous stacked
          layout. Nothing renders here for scenarios_showcase.) */}

      {/* Flag picker — only on the country_picker slide. Two-row wrap on
          desktop. Click a flag to swap the displayed country; selection
          persists across slide rotations. */}
      {slide.kind === "country_picker" && pickerCountries.length > 0 && (
        <div className="mt-7">
          <div
            className="text-[10px] uppercase tracking-[0.18em] mb-3"
            style={{ color: T.textMuted, fontWeight: 600 }}
          >
            {t('homepage:homeBeta.heroSlides.country.pickCountryHint')}
          </div>
          <div className="flex flex-wrap gap-2" style={{ maxWidth: 580 }}>
            {pickerCountries.map((c) => {
              const active = c.code === selectedCountryCode;
              return (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => onPickCountry(c.code)}
                  aria-pressed={active}
                  title={t('homepage:homeBeta.heroSlides.country.countryButtonTitle', { country: c.name, multiplier: c.multiplier.toFixed(1) })}
                  className="inline-flex items-center gap-1.5 transition-all"
                  style={{
                    padding: "6px 12px",
                    borderRadius: 999,
                    border: `1px solid ${active ? T.gold : T.divider}`,
                    background: active ? T.gold : "transparent",
                    color: active ? "#1B1B1B" : T.text,
                    cursor: "pointer",
                    fontWeight: active ? 700 : 500,
                    fontSize: "0.82rem",
                    letterSpacing: "0.02em",
                    fontFamily: sans,
                  }}
                >
                  <img
                    src={`https://flagcdn.com/w40/${c.code.toLowerCase()}.png`}
                    alt=""
                    width={22}
                    height={16}
                    style={{
                      borderRadius: 2,
                      display: "block",
                      boxShadow: active ? "0 0 0 1px rgba(0,0,0,0.18)" : "0 0 0 1px rgba(0,0,0,0.08)",
                    }}
                    aria-hidden
                  />
                  <span>{c.name}</span>
                  <span
                    style={{
                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                      fontSize: "0.7rem",
                      opacity: active ? 0.75 : 0.5,
                      marginLeft: 2,
                    }}
                  >
                    {c.multiplier.toFixed(1)}×
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-4 mt-9">
        {/* Primary slide CTA. Three cases:
            1. scenarios_showcase — suppress, body already has the anchor button.
            2. The data-layer slide (identifiable by ctaTo === "/api") — render
               TWO red buttons, one for the REST API and one for the MCP, since
               developers want a direct handle to each surface.
            3. Everything else — render the standard navy primary CTA. */}
        {slide.kind !== "scenarios_showcase" && (
          view.ctaTo === "/api" ? (
            <>
              <Link
                to="/api"
                className="inline-flex items-center gap-2 transition-transform hover:translate-x-0.5"
                style={{
                  background: T.red,
                  color: "#FAF9F6",
                  padding: "14px 26px",
                  borderRadius: 4,
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  textDecoration: "none",
                  letterSpacing: "0.01em",
                  boxShadow: `0 8px 24px ${isDark ? "rgba(0,0,0,0.55)" : "rgba(178,34,52,0.25)"}`,
                }}
              >
                {t('homepage:homeBeta.heroSlides.apiSlide.openApi', { defaultValue: 'Open the API' })} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/mcp"
                className="inline-flex items-center gap-2 transition-transform hover:translate-x-0.5"
                style={{
                  background: T.red,
                  color: "#FAF9F6",
                  padding: "14px 26px",
                  borderRadius: 4,
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  textDecoration: "none",
                  letterSpacing: "0.01em",
                  boxShadow: `0 8px 24px ${isDark ? "rgba(0,0,0,0.55)" : "rgba(178,34,52,0.25)"}`,
                }}
              >
                {t('homepage:homeBeta.heroSlides.apiSlide.openMcp', { defaultValue: 'Open the MCP' })} <ArrowRight className="w-4 h-4" />
              </Link>
            </>
          ) : slide.kind === "country_picker" && activeCountry ? (
            <>
              {/* Two CTAs for the international slide: dashboard (the
                  positioning surface — full crosswalk across all countries)
                  + per-country deep-dive for whichever flag is picked. */}
              <Link
                to="/international"
                className="inline-flex items-center gap-2 transition-transform hover:translate-x-0.5"
                style={{
                  background: T.navy,
                  color: "#FAF9F6",
                  padding: "14px 26px",
                  borderRadius: 4,
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  textDecoration: "none",
                  letterSpacing: "0.01em",
                  boxShadow: `0 8px 24px ${isDark ? "rgba(0,0,0,0.5)" : "rgba(0,40,104,0.18)"}`,
                }}
              >
                {t('homepage:homeBeta.heroSlides.country.cta')} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to={`/international/${activeCountry.slug}`}
                className="inline-flex items-center gap-2 transition-transform hover:translate-x-0.5"
                style={{
                  background: "transparent",
                  color: T.text,
                  padding: "13px 22px",
                  borderRadius: 4,
                  fontWeight: 600,
                  fontSize: "0.92rem",
                  textDecoration: "none",
                  letterSpacing: "0.01em",
                  border: `1px solid ${T.cardBorder}`,
                }}
              >
                {t('homepage:homeBeta.heroSlides.country.seeCountryVsUs', { country: activeCountry.name, defaultValue: 'See {{country}} vs US' })} <ArrowRight className="w-4 h-4" />
              </Link>
            </>
          ) : (
            <Link
              to={view.ctaTo}
              className="inline-flex items-center gap-2 transition-transform hover:translate-x-0.5"
              style={{
                background: T.navy,
                color: "#FAF9F6",
                padding: "14px 26px",
                borderRadius: 4,
                fontWeight: 600,
                fontSize: "0.95rem",
                textDecoration: "none",
                letterSpacing: "0.01em",
                boxShadow: `0 8px 24px ${isDark ? "rgba(0,0,0,0.5)" : "rgba(0,40,104,0.18)"}`,
              }}
            >
              {view.ctaLabel} <ArrowRight className="w-4 h-4" />
            </Link>
          )
        )}

        <Link
          to="/data-methodology"
          className="inline-flex items-center gap-2 text-sm transition-opacity hover:opacity-100"
          style={{
            color: T.gold,
            textDecoration: "none",
            padding: "10px 14px",
            borderRadius: 999,
            border: `1px solid ${T.gold}`,
            fontWeight: 500,
            background: T.chipBg,
            letterSpacing: "0.01em",
          }}
        >
          <ShieldCheck className="w-3.5 h-3.5" />
          {t('homepage:homeBeta.heroSlides.sourcedChip', { defaultValue: "Sourced from hospitals' own published files" })}
        </Link>
      </div>
      </>)}
    </>
  );
}


// EmpowermentStep — the 1/2/3 steps in the Connect-to-Care section.
function EmpowermentStep({
  T, step, icon, title, body,
}: {
  T: ReturnType<typeof useThemeTokens>;
  step: number;
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div
      style={{
        background: T.cardBg,
        border: `1px solid ${T.cardBorder}`,
        borderRadius: 8,
        padding: 26,
        position: "relative",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 18,
          right: 22,
          fontFamily: serif,
          fontSize: "2.6rem",
          color: T.gold,
          opacity: 0.35,
          lineHeight: 1,
          fontWeight: 400,
        }}
        aria-hidden
      >
        {step}
      </div>
      <div
        className="inline-flex items-center justify-center mb-4"
        style={{
          width: 44,
          height: 44,
          borderRadius: 12,
          background: T.chipBg,
          color: T.gold,
        }}
      >
        {icon}
      </div>
      <h3
        style={{
          fontFamily: serif,
          fontSize: "1.4rem",
          lineHeight: 1.15,
          letterSpacing: "-0.01em",
          color: T.text,
          margin: 0,
        }}
      >
        {title}
      </h3>
      <p style={{ color: T.textMuted, fontSize: "0.92rem", lineHeight: 1.55, marginTop: 12 }}>
        {body}
      </p>
    </div>
  );
}

// CareCategoryChip — one of the four resource-category tiles inside the
// Connect-to-Care strip. Colors mirror CATEGORY_META on ScenarioCareResources.
function CareCategoryChip({
  T, icon, color, label, body,
}: {
  T: ReturnType<typeof useThemeTokens>;
  icon: React.ReactNode;
  color: string;
  label: string;
  body: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <span
        className="inline-flex items-center justify-center flex-shrink-0"
        style={{
          width: 36,
          height: 36,
          borderRadius: 8,
          background: color + "22",
          color,
        }}
      >
        {icon}
      </span>
      <div className="min-w-0">
        <div style={{ fontWeight: 700, fontSize: "0.85rem", color: T.text }}>{label}</div>
        <div style={{ color: T.textMuted, fontSize: "0.78rem", lineHeight: 1.4, marginTop: 2 }}>{body}</div>
      </div>
    </div>
  );
}

// ScenarioCategoryPill — the icon+label+count chip used to swap the scenario
// row. Active state fills with the category's accent color; inactive is
// transparent with a faint border. Mirrors the pattern from MapBottomPanel
// and HospitalScenarioDeals so the home-beta pill bar reads identically to
// the hospital-side browser.
// Canonical chargemaster code-type colors + tooltip copy. Pulled verbatim
// from /hospital-rankings (the rank-by-code-type pills at line 3890) so the
// home-beta table speaks the same visual + textual language as the live tool.
type ChargemasterCol = {
  key: "overall" | "cpt" | "hcpcs" | "drg" | "rev";
  label: string;
  emoji: string;
  color: string;
  tooltip: string;
};
const CHARGEMASTER_COLS: ChargemasterCol[] = [
  { key: "overall", label: "Overall", emoji: "📊", color: "#C5A55A", tooltip: "Weighted average across CPT, HCPCS, MS-DRG, Revenue, Drug/NDC, and Supply. Each code type is weighted by the number of comparable procedures the hospital reports." },
  { key: "cpt",     label: "CPT®",    emoji: "🏥", color: "#60a5fa", tooltip: "CPT® (Current Procedural Terminology) — standardized codes for medical procedures and services. CPT® is a registered trademark of the American Medical Association." },
  { key: "hcpcs",   label: "HCPCS",   emoji: "💊", color: "#4ade80", tooltip: "Healthcare Common Procedure Coding System — codes for supplies, drugs, and services not covered by CPT® (injections, DME, ambulance)." },
  { key: "drg",     label: "MS-DRG",  emoji: "🏨", color: "#c084fc", tooltip: "Medicare Severity Diagnosis Related Groups — inpatient episode bundles that group related diagnoses and procedures into a single payment category." },
  { key: "rev",     label: "Revenue", emoji: "📋", color: "#fbbf24", tooltip: "Revenue codes — department-level billing categories (OR services, ER visits, room & board). Bucketed by setting × unit × dosage so they compare apples-to-apples across hospitals." },
];

function formatGeneratedDate(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
}

function fmtMarkup(v: number | null): { text: string; tone: "pos" | "neg" | "zero" | "none" } {
  if (v == null || !Number.isFinite(v)) return { text: "—", tone: "none" };
  const r = Math.round(v);
  if (r > 0) return { text: `+${r.toLocaleString()}%`, tone: "pos" };
  if (r < 0) return { text: `−${Math.abs(r).toLocaleString()}%`, tone: "neg" };
  return { text: "0%", tone: "zero" };
}

// Format X/Y "ranked / listed" compactly. Uses K-suffix for ≥1k so a 9-digit
// CPT total doesn't blow out the table width.
function fmtCount(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return "0";
  if (n >= 10000) return `${Math.round(n / 1000)}k`;
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  return n.toLocaleString();
}

// TopHospitalTable — compact table version of /hospital-rankings showing
// today's 5 worst-markup hospitals broken out by chargemaster code type.
// Column-header colors + tooltips match the rank-by-code-type pills on
// /hospital-rankings. Each markup cell opens the same ProcedureDrilldownModal
// the rankings page uses, with X/Y "ranked / listed" counts beneath.
function TopHospitalTable({
  T, isDark, rows, onOpenDrilldown,
}: {
  T: ReturnType<typeof useThemeTokens>;
  isDark: boolean;
  rows: TopHospitalRow[];
  onOpenDrilldown: (hospitalId: string, hospitalName: string, procedureType: string, avgMarkup: number) => void;
}) {
  const { t } = useTranslation(['homepage']);
  const colLabel = (key: string, fallback: string) => t(`homepage:homeBeta.heroSlides.nationalIndex.cols.${key}.label`, fallback);
  const colTip = (key: string, fallback: string) => t(`homepage:homeBeta.heroSlides.nationalIndex.cols.${key}.tooltip`, fallback);
  // Maps a column key to the procedureType the drilldown modal expects.
  // 'overall' has no drilldown (it's the weighted summary). 'rev' becomes
  // 'revenue' to match the rankings-page call signature exactly.
  const drillType: Record<string, string | null> = {
    overall: null, cpt: "cpt", hcpcs: "hcpcs", drg: "drg", rev: "revenue",
  };
  // Grid template: rank | hospital | overall | cpt | hcpcs | drg | rev | outlier-rate
  const colTemplate = "28px minmax(0, 1fr) 78px 72px 72px 72px 72px 80px";
  // Outlier-rate column metadata. Distinct red so it doesn't read as a code
  // type — it's a derived stat (share of procedures priced ≥50% above the
  // national average), not a chargemaster code system.
  const OUTLIER_COLOR = "#ef4444";

  return (
    <div
      style={{
        background: T.cardBg,
        border: `1px solid ${T.cardBorder}`,
        borderRadius: 8,
        overflow: "hidden",
        maxWidth: 820,
        boxShadow: `0 8px 24px ${isDark ? "rgba(0,0,0,0.35)" : "rgba(0,40,104,0.06)"}`,
      }}
    >
      {/* Header row */}
      <div
        className="grid"
        style={{
          gridTemplateColumns: colTemplate,
          padding: "8px 12px",
          borderBottom: `1px solid ${T.cardBorder}`,
          background: isDark ? "rgba(255,255,255,0.02)" : "rgba(27,27,27,0.025)",
          fontSize: "0.62rem",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          fontWeight: 700,
          color: T.textMuted,
          gap: 8,
          alignItems: "center",
        }}
      >
        <span>#</span>
        <span>{t('homepage:homeBeta.heroSlides.nationalIndex.cols.hospital', 'Hospital')}</span>
        {CHARGEMASTER_COLS.map((c) => (
          <Tooltip key={c.key}>
            <TooltipTrigger asChild>
              <span
                style={{
                  textAlign: "right",
                  color: c.color,
                  cursor: "help",
                  fontWeight: 800,
                  whiteSpace: "nowrap",
                  fontSize: "0.6rem",
                }}
              >
                {c.emoji} {colLabel(c.key, c.label)}
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom" style={{ maxWidth: 280, fontSize: "0.78rem", lineHeight: 1.5 }}>
              <div style={{ fontWeight: 700, marginBottom: 4, color: c.color }}>{c.emoji} {colLabel(c.key, c.label)}</div>
              <div>{colTip(c.key, c.tooltip)}</div>
              <div style={{ marginTop: 6, paddingTop: 6, borderTop: "1px solid rgba(0,0,0,0.1)", fontSize: "0.72rem", opacity: 0.85 }}>
                <Trans
                  i18nKey="homepage:homeBeta.heroSlides.nationalIndex.cellExplainer"
                  components={{ b: <strong /> }}
                />
              </div>
            </TooltipContent>
          </Tooltip>
        ))}
        {/* Outlier Rate header */}
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              style={{
                textAlign: "right",
                color: OUTLIER_COLOR,
                cursor: "help",
                fontWeight: 800,
                whiteSpace: "nowrap",
                fontSize: "0.6rem",
              }}
            >
              {t('homepage:homeBeta.heroSlides.nationalIndex.cols.outlierShort', '⚠️ Outlier %')}
            </span>
          </TooltipTrigger>
          <TooltipContent side="bottom" style={{ maxWidth: 300, fontSize: "0.78rem", lineHeight: 1.5 }}>
            <div style={{ fontWeight: 700, marginBottom: 4, color: OUTLIER_COLOR }}>{t('homepage:homeBeta.heroSlides.nationalIndex.cols.outlierLong', '⚠️ Outlier Rate')}</div>
            <div>
              <Trans
                i18nKey="homepage:homeBeta.heroSlides.nationalIndex.cols.outlierBody"
                components={{ b: <strong />, mono: <span style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }} /> }}
              />
            </div>
            <div style={{ marginTop: 6, paddingTop: 6, borderTop: "1px solid rgba(0,0,0,0.1)", fontSize: "0.72rem", opacity: 0.85 }}>
              {t('homepage:homeBeta.heroSlides.nationalIndex.cols.outlierFooter', 'The higher this number, the more frequently this hospital prices procedures well above peer hospitals.')}
            </div>
          </TooltipContent>
        </Tooltip>
      </div>

      {/* Data rows */}
      {rows.map((r, i) => {
        const cells: Array<{
          col: ChargemasterCol;
          v: ReturnType<typeof fmtMarkup>;
          markup: number | null;
          ranked: number;
          listed: number;
        }> = [
          { col: CHARGEMASTER_COLS[0], v: fmtMarkup(r.overallMarkup), markup: r.overallMarkup, ranked: r.overallRanked, listed: r.overallListed },
          { col: CHARGEMASTER_COLS[1], v: fmtMarkup(r.cptMarkup),    markup: r.cptMarkup,    ranked: r.cptRanked,    listed: r.cptListed },
          { col: CHARGEMASTER_COLS[2], v: fmtMarkup(r.hcpcsMarkup),  markup: r.hcpcsMarkup,  ranked: r.hcpcsRanked,  listed: r.hcpcsListed },
          { col: CHARGEMASTER_COLS[3], v: fmtMarkup(r.drgMarkup),    markup: r.drgMarkup,    ranked: r.drgRanked,    listed: r.drgListed },
          { col: CHARGEMASTER_COLS[4], v: fmtMarkup(r.revMarkup),    markup: r.revMarkup,    ranked: r.revRanked,    listed: r.revListed },
        ];
        return (
          <div
            key={r.hospitalId}
            className="grid"
            style={{
              gridTemplateColumns: colTemplate,
              padding: "10px 12px",
              borderBottom: i < rows.length - 1 ? `1px solid ${T.cardBorder}` : "none",
              color: T.text,
              gap: 8,
              alignItems: "center",
            }}
          >
            <span style={{ color: T.gold, fontWeight: 700, fontFamily: serif, fontSize: "0.95rem" }}>
              {i + 1}
            </span>
            {/* Hospital name links to profile — keeps the row-level navigation
                but keeps the markup cells free for the drilldown trigger. */}
            <Link
              to={`/hospital/${r.hospitalId}`}
              className="min-w-0 group"
              style={{ textDecoration: "none", color: T.text }}
            >
              <div
                style={{
                  fontWeight: 600,
                  fontSize: "0.82rem",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  color: T.text,
                }}
                title={r.name}
                className="group-hover:underline"
              >
                {r.name}
              </div>
              {(r.city || r.state) && (
                <div style={{ fontSize: "0.66rem", color: T.textMuted, marginTop: 1 }}>
                  {r.city}{r.city && r.state ? ", " : ""}{r.state}
                </div>
              )}
            </Link>
            {cells.map(({ col, v, markup, ranked, listed }) => {
              const drill = drillType[col.key];
              const toneColor = v.tone === "pos" ? col.color
                              : v.tone === "neg" ? "#2b5e49"
                              : v.tone === "zero" ? T.textMuted
                              : "rgba(127,127,127,0.4)";
              const clickable = !!drill && markup != null;
              const inner = (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    gap: 1,
                  }}
                >
                  <span
                    style={{
                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                      fontSize: "0.82rem",
                      fontWeight: 700,
                      color: toneColor,
                      lineHeight: 1.1,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {v.text}
                    {clickable && <span style={{ marginLeft: 3, fontSize: "0.62rem", opacity: 0.85 }}>🔍</span>}
                  </span>
                  <span
                    style={{
                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                      fontSize: "0.6rem",
                      color: T.textMuted,
                      letterSpacing: "0.01em",
                      lineHeight: 1.1,
                    }}
                  >
                    {listed > 0 ? `${fmtCount(ranked)}/${fmtCount(listed)}` : "—"}
                  </span>
                </div>
              );
              if (!clickable) {
                return (
                  <Tooltip key={col.key}>
                    <TooltipTrigger asChild>
                      <div style={{ textAlign: "right", cursor: "help" }}>{inner}</div>
                    </TooltipTrigger>
                    <TooltipContent side="left" style={{ maxWidth: 260, fontSize: "0.78rem", lineHeight: 1.5 }}>
                      <div style={{ fontWeight: 700, marginBottom: 4, color: col.color }}>{col.emoji} {col.label}</div>
                      <div>
                        {ranked.toLocaleString()} of {listed.toLocaleString()} procedures included in this
                        hospital's weighted overall markup.
                      </div>
                    </TooltipContent>
                  </Tooltip>
                );
              }
              return (
                <Tooltip key={col.key}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => onOpenDrilldown(r.hospitalId, r.name, drill!, markup!)}
                      style={{
                        background: "transparent",
                        border: 0,
                        padding: "2px 4px",
                        borderRadius: 4,
                        cursor: "pointer",
                        textAlign: "right",
                        marginLeft: "auto",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = isDark ? "rgba(197,165,90,0.08)" : "rgba(0,40,104,0.04)")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      aria-label={t('homepage:homeBeta.heroSlides.nationalIndex.cellAria', { col: colLabel(col.key, col.label), hospital: r.name, defaultValue: 'Open {{col}} procedure breakdown for {{hospital}}' })}
                    >
                      {inner}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="left" style={{ maxWidth: 260, fontSize: "0.78rem", lineHeight: 1.5 }}>
                    <div style={{ fontWeight: 700, marginBottom: 4, color: col.color }}>
                      {col.emoji} {colLabel(col.key, col.label)} — {t('homepage:homeBeta.heroSlides.nationalIndex.clickToDrill', 'click to drill')}
                    </div>
                    <div>
                      <Trans
                        i18nKey="homepage:homeBeta.heroSlides.nationalIndex.rankedOfListed"
                        values={{ ranked: ranked.toLocaleString(), listed: listed.toLocaleString(), col: colLabel(col.key, col.label) }}
                        components={{ b: <strong /> }}
                      />
                    </div>
                    <div style={{ marginTop: 6, opacity: 0.85 }}>
                      {t('homepage:homeBeta.heroSlides.nationalIndex.opensDrilldown', 'Opens the procedure-by-procedure breakdown — same drilldown used on /hospital-rankings.')}
                    </div>
                  </TooltipContent>
                </Tooltip>
              );
            })}
            {/* Outlier Rate cell — derived stat, not a code-type markup, so
                no drilldown. Tooltip explains the 50%-above-national threshold. */}
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    gap: 1,
                    cursor: "help",
                  }}
                >
                  <span
                    style={{
                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                      fontSize: "0.82rem",
                      fontWeight: 700,
                      color: r.outlierRate == null ? T.textMuted : OUTLIER_COLOR,
                      lineHeight: 1.1,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {r.outlierRate == null ? "—" : `${r.outlierRate.toFixed(1)}%`}
                  </span>
                  <span
                    style={{
                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                      fontSize: "0.6rem",
                      color: T.textMuted,
                      letterSpacing: "0.01em",
                      lineHeight: 1.1,
                    }}
                  >
                    ≥50% over avg
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent side="left" style={{ maxWidth: 280, fontSize: "0.78rem", lineHeight: 1.5 }}>
                <div style={{ fontWeight: 700, marginBottom: 4, color: OUTLIER_COLOR }}>⚠️ Outlier Rate</div>
                <div>
                  {r.outlierRate == null ? (
                    <>No outlier rate available for this hospital.</>
                  ) : (
                    <>
                      <strong>{r.outlierRate.toFixed(1)}%</strong> of this hospital's ranked
                      procedures are priced at <strong>≥ 50% above the national average</strong> for
                      the same billing code — the methodology's outlier threshold.
                    </>
                  )}
                </div>
                <div style={{ marginTop: 6, opacity: 0.85, fontSize: "0.72rem" }}>
                  Computed as (outlier count / total ranked procedures) × 100.
                </div>
              </TooltipContent>
            </Tooltip>
          </div>
        );
      })}
    </div>
  );
}

// ScenariosCarousel — auto-cycling 2-up rail of real ScenarioCards for the
// scenarios_showcase slide. Cycles every ~7s, pauses on hover, and exposes
// dot indicators (one per pair) so users can jump directly to a pair. Each
// card is the same compact ScenarioCard the section below uses, with the
// severity-ladder bottom bar lit up from siblingMap.
const SCENARIO_CYCLE_INTERVAL_MS = 3500;
const SCENARIO_TRANSITION_MS = 500;
const CARD_WIDTH_PX = 280;
const CARD_GAP_PX = 16;
const CARD_SLOT_PX = CARD_WIDTH_PX + CARD_GAP_PX;

function ScenariosCarousel({
  T, scenarios, theme, bookmarks, onToggleBookmark, siblingMap,
}: {
  T: ReturnType<typeof useThemeTokens>;
  scenarios: ScenarioCardData[];
  theme: CardTheme;
  bookmarks: Set<string>;
  onToggleBookmark: (id: string) => void;
  siblingMap?: Map<string, FamilySibling[]>;
}) {
  // Multi-card infinite swiper. Card width is fixed (280px + 16px gap), so
  // the visible card count is whatever the container fits — roughly 2 on
  // narrow tablets, 3-4 on desktop, 5-6 on wide. Advances one card every
  // ~3.5s and pauses on hover.
  //
  // Infinite-loop trick: render the pool, then render it AGAIN immediately
  // after (cloned). idx advances 0 → N normally; when idx === N the visible
  // window has slid onto the cloned copy of pool[0..]. We pause the
  // transform animation and snap idx back to 0 in the same frame — visitor
  // sees a seamless continuous loop.
  const pool = scenarios.slice(0, 10);
  const N = pool.length;

  const [idx, setIdx] = useState(0); // 0..N (N triggers the snap-back)
  const [animating, setAnimating] = useState(true);
  const [paused, setPaused] = useState(false);

  // Auto-advance.
  useEffect(() => {
    if (paused || N <= 1) return;
    const id = setInterval(() => {
      setAnimating(true);
      setIdx((i) => i + 1);
    }, SCENARIO_CYCLE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [paused, N]);

  // Snap-back when idx hits the clone region.
  useEffect(() => {
    if (idx !== N || N === 0) return;
    const t = setTimeout(() => {
      setAnimating(false);
      setIdx(0);
      requestAnimationFrame(() => requestAnimationFrame(() => setAnimating(true)));
    }, SCENARIO_TRANSITION_MS + 20);
    return () => clearTimeout(t);
  }, [idx, N]);

  // Active dot tracking — when we're momentarily past the pool, highlight 0.
  const activeDot = idx % N;

  if (N === 0) return null;

  // The track holds 2N cards (pool + cloned pool) so the swipe from card N-1
  // to card N is visually a normal advance onto pool[0]-the-clone.
  const trackCards: { card: ScenarioCardData; key: string; isClone: boolean }[] = [];
  for (let i = 0; i < N; i++) trackCards.push({ card: pool[i], key: pool[i].id + "-a", isClone: false });
  for (let i = 0; i < N; i++) trackCards.push({ card: pool[i], key: pool[i].id + "-b", isClone: true });

  return (
    <div
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      {/* Viewport — clips the translating track to the container width. */}
      <div style={{ overflow: "hidden" }}>
        <div
          style={{
            display: "flex",
            gap: CARD_GAP_PX,
            transform: `translateX(-${idx * CARD_SLOT_PX}px)`,
            transition: animating ? `transform ${SCENARIO_TRANSITION_MS}ms cubic-bezier(.2,.7,.2,1)` : "none",
          }}
        >
          {trackCards.map(({ card, key, isClone }) => (
            <div
              key={key}
              style={{ flex: `0 0 ${CARD_WIDTH_PX}px`, width: CARD_WIDTH_PX }}
              aria-hidden={isClone ? true : undefined}
            >
              <ScenarioCard
                scenario={card}
                compact
                isStarred={bookmarks.has(card.id)}
                theme={theme}
                onToggleBookmark={onToggleBookmark}
                familySiblings={siblingMap?.get(card.id)}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Dot indicators — one per scenario in the pool. Click to jump. */}
      {N > 1 && (
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          {pool.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Show scenario ${i + 1}`}
              onClick={() => {
                setAnimating(true);
                setIdx(i);
                setPaused(true);
              }}
              style={{
                width: i === activeDot ? 22 : 6,
                height: 4,
                borderRadius: 2,
                background: i === activeDot ? T.gold : T.divider,
                border: 0,
                cursor: "pointer",
                transition: "width 300ms ease, background 300ms ease",
                padding: 0,
              }}
            />
          ))}
          <span className="text-xs ml-3" style={{ color: T.textMuted }}>
            {N} featured · hover to pause
          </span>
        </div>
      )}
    </div>
  );
}

// HeroDignityModel — full-bleed 3D Dignity that fills its parent container,
// instead of the 80×80 chat-avatar DignityAvatar gives us. Mirrors the pattern
// ConsumerHomepage uses: WebGL detection, lazy-loaded @google/model-viewer,
// PNG fallback when WebGL is unavailable. Includes the same oscillating-head
// animation as the avatar so it feels alive in the hero.
function HeroDignityModel() {
  const viewerRef = useRef<HTMLElement | null>(null);
  const [modelReady, setModelReady] = useState(false);
  const animRef = useRef<number>(0);
  const [webglSupported] = useState(() => {
    try {
      if (typeof document === "undefined") return false;
      const c = document.createElement("canvas");
      return !!(c.getContext("webgl2") || c.getContext("webgl") || c.getContext("experimental-webgl"));
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (!webglSupported) return;
    if (typeof customElements !== "undefined" && customElements.get("model-viewer")) {
      setModelReady(true);
      return;
    }
    ensureModelViewer().then(() => {
      customElements.whenDefined("model-viewer").then(() => setModelReady(true));
    }).catch(() => {});
  }, [webglSupported]);

  // ±18° oscillating head, slower in idle. Matches the DignityAvatar idle
  // animation but at hero scale.
  useEffect(() => {
    if (!modelReady) return;
    const amplitude = 18;
    const period = 5000;
    const start = performance.now();
    function animate(now: number) {
      const elapsed = now - start;
      const theta = amplitude * Math.sin((2 * Math.PI * elapsed) / period);
      const viewer = viewerRef.current as { cameraOrbit?: string } | null;
      if (viewer && viewer.cameraOrbit !== undefined) {
        viewer.cameraOrbit = `${theta}deg 75deg 105%`;
      }
      animRef.current = requestAnimationFrame(animate);
    }
    animRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animRef.current);
  }, [modelReady]);

  if (!webglSupported || !modelReady) {
    return (
      <img
        src="/consumer/dignity-transparent.webp"
        alt="Dignity — America's healthcare advocate"
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          display: "block",
          filter: "drop-shadow(0 12px 32px rgba(0,40,104,0.25))",
        }}
      />
    );
  }

  return (
    <model-viewer
      ref={viewerRef as React.RefObject<HTMLElement>}
      src="/consumer/dignity_bill.glb"
      alt="Dignity — America's healthcare advocate"
      shadow-intensity="1"
      exposure="1.1"
      interaction-prompt="none"
      camera-orbit="0deg 75deg 105%"
      style={{
        width: "100%",
        height: "100%",
        background: "transparent",
        display: "block",
      }}
    />
  );
}

function ScenarioCategoryPill({
  T, icon, label, count, active, accent, onClick,
}: {
  T: ReturnType<typeof useThemeTokens>;
  icon: React.ReactNode;
  label: string;
  count: number;
  active: boolean;
  accent: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className="inline-flex items-center gap-1.5 transition-all"
      style={{
        padding: "6px 12px",
        borderRadius: 999,
        border: `1px solid ${active ? accent : T.divider}`,
        background: active ? accent : "transparent",
        color: active ? "#fff" : T.text,
        cursor: "pointer",
        fontWeight: active ? 700 : 500,
        fontSize: "0.78rem",
        letterSpacing: "0.02em",
        fontFamily: sans,
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ display: "inline-flex", alignItems: "center", color: active ? "#fff" : accent }}>
        {icon}
      </span>
      <span>{label}</span>
      <span
        style={{
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: "0.66rem",
          opacity: active ? 0.85 : 0.5,
          marginLeft: 2,
        }}
      >
        {count.toLocaleString()}
      </span>
    </button>
  );
}

function SectionHeader({
  T, eyebrow, title, sub, onInverse,
}: {
  T: ReturnType<typeof useThemeTokens>;
  eyebrow: string; title: string; sub: string; onInverse?: boolean;
}) {
  const textColor = onInverse ? T.textOnInverse : T.text;
  const mutedColor = onInverse ? T.textMutedOnInverse : T.textMuted;
  return (
    <div className="max-w-3xl">
      <div
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] mb-5"
        style={{ color: T.gold, fontWeight: 600 }}
      >
        <span style={{ width: 24, height: 1, background: T.gold, display: "inline-block" }} />
        {eyebrow}
      </div>
      <h2
        style={{
          fontFamily: serif,
          fontSize: "clamp(1.9rem, 3.6vw, 3rem)",
          lineHeight: 1.08,
          letterSpacing: "-0.02em",
          color: textColor,
          margin: 0,
        }}
      >
        {title}
      </h2>
      <p style={{ color: mutedColor, fontSize: "1.05rem", lineHeight: 1.55, marginTop: 16 }}>
        {sub}
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RankingsDemoExplorer — left-rail guide + iframed /hospital-rankings preview
//
// Click a dimension in the rail and the iframe lights up that view: expands
// the right column group, sorts by that column, scrolls the header into the
// visible region. Auto-cycles every ~7s when nobody has interacted; pauses
// on hover; locks to the user's pick on click. Communicates with the iframe
// via postMessage (no iframe reloads).
// ─────────────────────────────────────────────────────────────────────────────

type DemoDimension = {
  key: string;
  label: string;
  group: "default" | "pricing" | "chargemaster" | "quality" | "scenarios" | "financial";
  hint: string;
  // Scenario UUID — only present for scenario-mode demo steps. When set, the
  // explorer posts `demo:scenario` to the iframe instead of `demo:open`.
  scenarioId?: string;
  /** Marks dimensions added in the 2026-05-26 sidebar expansion so we can
   *  badge them as NEW in the sidebar UI until visitors get familiar. */
  isNew?: boolean;
  /** Marks dimensions we haven't shipped yet but want to preview in the
   *  sidebar so the data plan reads honestly. Rendered with a "Coming
   *  Soon" pill, click is a no-op (pickDimension still runs but the iframe
   *  has nothing meaningful to show). */
  comingSoon?: boolean;
};

// Bundle item [14] 2026-05-26: 9 new dimensions, Quality promoted above
// Scenarios, Scenarios paginated via voiced-family fetch (see VoicedFamilies
// state in RankingsDemoExplorer). Ordering inside DEMO_DIMENSIONS is the
// render order in the sidebar; group transitions emit the group header.
const DEMO_DIMENSIONS: DemoDimension[] = [
  // ── The Tool ────────────────────────────────────────────────────────────
  { key: "overall_markup",  group: "default",      label: "Overall AVG Markup",  hint: "Default lens — every code type, every hospital, one ranked column" },

  // ── Pricing Signals ─────────────────────────────────────────────────────
  // Shame Score removed (was a composite of dims that already appear on
  // their own; double-counted). Pricing Volatility kept as "Coming Soon"
  // — it needs a year-over-year price snapshot ingestion that we don't
  // have yet (would compare same-code prices across reporting periods).
  { key: "outlier_rate",         group: "pricing",      label: "Outlier Rate",            hint: "Share of this hospital's procedures priced ≥50% above the national average" },
  { key: "pricing_volatility",   group: "pricing",      label: "Pricing Volatility",      hint: "Year-over-year price drift per scenario and per code — requires multi-period chargemaster snapshots we're staging now.", isNew: true, comingSoon: true },
  { key: "international_multiplier", group: "pricing",  label: "International Multiplier (16×)", hint: "Median multiplier vs international peers across 18 medical categories", isNew: true },

  // ── Chargemaster (now includes NDC drug-pricing signal) ─────────────────
  { key: "cpt_markup",      group: "chargemaster", label: "CPT markup",          hint: "Procedural-services markup only (office visits, surgeries, imaging)" },
  { key: "hcpcs_markup",    group: "chargemaster", label: "HCPCS markup",        hint: "Injectable drugs, DME, ambulance — not covered by CPT®" },
  { key: "msdrg_markup",    group: "chargemaster", label: "MS-DRG markup",       hint: "Inpatient bundle markup — DRG-by-DRG" },
  { key: "rev_markup",      group: "chargemaster", label: "Revenue (REV) markup", hint: "Hospital department billing — OR, ICU, recovery, pharmacy" },
  { key: "ndc_markup",      group: "chargemaster", label: "NDC drug markup",     hint: "Per-unit drug pricing markup — chargemaster NDC codes aggregated per hospital", isNew: true },

  // ── Quality (PROMOTED above Scenarios; 7 dimensions, 5 new) ─────────────
  // All seven dimensions are backed by data already in the database
  // (hospital_cms_data has 400K hcahps rows, 212K hai rows, 175K timely-
  // effective rows). The hospital-rankings iframe wires these into the
  // sortBy enum via runtime supabase fetches per CMS measure.
  { key: "cms_rating",      group: "quality",      label: "CMS Overall Star Rating",  hint: "Medicare's 1–5 star composite quality rating (hospitals.cms_overall_rating)" },
  { key: "mortality",       group: "quality",      label: "Mortality",                hint: "Risk-adjusted hospital-wide mortality (lower = better)" },
  { key: "readmission",     group: "quality",      label: "Readmission",              hint: "Risk-adjusted readmission rate (lower = better)", isNew: true },
  { key: "hcahps",          group: "quality",      label: "Patient Experience (HCAHPS)", hint: "% of patients who would definitely recommend this hospital (CMS HCAHPS H_RECMND_DY)", isNew: true },
  { key: "hai_risk",        group: "quality",      label: "Safety / HAI Risk",        hint: "Standardized infection ratio across CMS HAI measures (lower = safer)", isNew: true },
  { key: "google_rating",   group: "quality",      label: "Google Rating",            hint: "Google reviews score + review count signal", isNew: true },
  { key: "er_wait_time",    group: "quality",      label: "ER Wait Time",             hint: "Median minutes from arrival to provider (CMS timely_effective OP_18b)", isNew: true },

  // ── Scenarios (DEMOTED here, paginated through top-50 voiced families) ──
  // Sidebar render code pulls a live list of voiced families from
  // dignity_narration_lines join scenarios on render and shows 7-per-page
  // pagination. These hardcoded 3 are anchor examples (always visible) so
  // the section never empty-states even when the live query is loading.
  { key: "scenario_csection_base",    group: "scenarios", label: "C-section · baseline",            hint: "Severity 0 — uncomplicated cesarean delivery",                       scenarioId: "550e8400-e29b-41d4-a716-446655440002" },
  { key: "scenario_thr_base",         group: "scenarios", label: "Total hip replacement · baseline", hint: "Severity 0 — straightforward elective THR",                          scenarioId: "493b56a6-5a26-489f-a9b8-85b3761e6431" },
  { key: "scenario_appendectomy",     group: "scenarios", label: "Appendectomy · emergency",         hint: "Emergency appendectomy with perforation pathway",                    scenarioId: "0c4dda28-32fa-41c7-8dc7-b8afb3d0f7cd" },

  // ── Financial ────────────────────────────────────────────────────────────
  { key: "annual_revenue",  group: "financial",    label: "Annual revenue",      hint: "Top-line revenue from CMS HCRIS cost reports" },
  { key: "operating_margin",group: "financial",    label: "Operating margin",    hint: "How much the hospital nets vs takes in" },
  { key: "charity_care",    group: "financial",    label: "Charity care",        hint: "Dollar amount of uncompensated care provided" },
  { key: "ceo_comp",        group: "financial",    label: "CEO compensation",    hint: "Executive comp per bed — IRS Form 990 + state filings" },
];

const DEMO_GROUP_META: Record<DemoDimension["group"], { label: string; color: string; badge?: string }> = {
  default:      { label: "The Tool",        color: "#7B8AA0" }, // neutral slate
  pricing:      { label: "Pricing Signals", color: "#B45309" }, // amber-700 — distinct from chargemaster
  chargemaster: { label: "Code Markups",    color: "#3A7BB5" }, // blue
  quality:      { label: "Quality",         color: "#7C3AED", badge: "EXPANDED" }, // violet — promoted above scenarios in 2026-05-26 reorder
  scenarios:    { label: "Scenarios",       color: "#C5A55A" }, // gold (matches scenario-card theme)
  financial:    { label: "Financial",       color: "#0F8A5F" }, // emerald
};

const SCENARIOS_PAGE_SIZE = 7;

const DEMO_CYCLE_MS = 6500;

function RankingsDemoExplorer({ T, isDark }: {
  T: ReturnType<typeof useThemeTokens>;
  isDark: boolean;
}) {
  const { t } = useTranslation(['homepage']);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [userLocked, setUserLocked] = useState(false);
  const [paused, setPaused] = useState(false);
  // "Expanded" = in-page takeover: the iframe wrapper is position-fixed to
  // cover the viewport, but the browser chrome (URL bar, tabs, dock/menubar)
  // stays visible. Calmer UX than the browser-level Fullscreen API, no
  // scary "press Esc" overlay, no OS-level mode switch.
  const [isExpanded, setIsExpanded] = useState(false);

  // Voiced families — top scenario families that have dignity narration_lines
  // recorded. Drives the Scenarios pagination in the sidebar. Joins
  // dignity_narration_lines with scenarios.scenario_family_id, returns the
  // severity-0 base scenario per family + variant count + cost range.
  type VoicedFamily = {
    familyId: string;
    baseUuid: string;
    baseName: string;
    baseSlug: string;
    variantCount: number;
    minCost: number;
    maxCost: number;
  };
  const [voicedFamilies, setVoicedFamilies] = useState<VoicedFamily[] | null>(null);
  const [scenarioPage, setScenarioPage] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Pull all scenarios with narration_lines + their family id. Two-step
        // query because PostgREST can't express "DISTINCT ON family + count"
        // in one round-trip and a small client-side aggregation is fine for
        // the ~600-row distinct-family set we're working with.
        const { data: narrated, error: nErr } = await supabase
          .from('dignity_narration_lines')
          .select('scenario_id')
          .not('scenario_id', 'is', null)
          .limit(5000);
        if (cancelled || nErr || !narrated) { if (!cancelled) setVoicedFamilies([]); return; }
        const scenarioIds = Array.from(new Set(narrated.map((r: any) => r.scenario_id).filter(Boolean)));
        if (scenarioIds.length === 0) { setVoicedFamilies([]); return; }

        // PostgREST `in.(...)` builds a single URL with every ID inline, so
        // 600+ UUIDs blows past the ~2-8KB browser URL ceiling and CORS
        // preflight fails (Access to fetch ... ERR_FAILED). Chunk to 80
        // IDs per request and fire in parallel — each batch URL stays
        // comfortably under 4KB.
        const CHUNK_SIZE = 80;
        const chunks: string[][] = [];
        for (let i = 0; i < scenarioIds.length; i += CHUNK_SIZE) {
          chunks.push(scenarioIds.slice(i, i + CHUNK_SIZE));
        }
        const batchResults = await Promise.all(chunks.map((chunk) =>
          supabase
            .from('scenarios')
            .select('id, name, slug, scenario_family_id, severity_level, estimated_cost')
            .in('id', chunk)
            .not('scenario_family_id', 'is', null)
        ));
        if (cancelled) return;
        const scenarios: any[] = [];
        for (const r of batchResults) {
          if (!r.error && Array.isArray(r.data)) scenarios.push(...r.data);
        }
        if (scenarios.length === 0) { setVoicedFamilies([]); return; }

        // Aggregate per family: collect costs, pick severity-0 as the base
        // representative (fall back to lowest severity_level otherwise).
        const byFamily = new Map<string, any[]>();
        for (const s of scenarios) {
          const fid = (s as any).scenario_family_id;
          if (!byFamily.has(fid)) byFamily.set(fid, []);
          byFamily.get(fid)!.push(s);
        }

        // Fetch the real sev-0 base for each family — narration coverage is
        // sparse, so a family's true root often doesn't appear in `rows` and
        // the fallback below would anchor on a sev-1+ variant (e.g. OB family
        // showing "Unplanned ER Arrival" instead of "Uncomplicated Vaginal
        // Delivery"). Chunked the same way as the narrated lookup above.
        const familyIds = Array.from(byFamily.keys());
        const baseChunks: string[][] = [];
        for (let i = 0; i < familyIds.length; i += CHUNK_SIZE) {
          baseChunks.push(familyIds.slice(i, i + CHUNK_SIZE));
        }
        const baseResults = await Promise.all(baseChunks.map((chunk) =>
          supabase
            .from('scenarios')
            .select('id, name, slug, scenario_family_id, severity_level')
            .in('scenario_family_id', chunk)
            .eq('severity_level', 0)
        ));
        if (cancelled) return;
        const baseByFamily = new Map<string, any>();
        for (const r of baseResults) {
          if (!r.error && Array.isArray(r.data)) {
            for (const row of r.data as any[]) baseByFamily.set(row.scenario_family_id, row);
          }
        }

        const families: VoicedFamily[] = [];
        for (const [fid, rows] of byFamily.entries()) {
          if (rows.length < 2) continue; // Skip singletons — pagination is for families
          const costs = rows.map((r: any) => Number(r.estimated_cost) || 0).filter((c) => c > 0);
          const base = baseByFamily.get(fid)
            || rows.find((r: any) => (r.severity_level ?? 0) === 0)
            || rows.slice().sort((a: any, b: any) => (a.severity_level ?? 99) - (b.severity_level ?? 99))[0];
          families.push({
            familyId: fid,
            baseUuid: base.id,
            baseName: base.name,
            baseSlug: base.slug || base.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''),
            variantCount: rows.length,
            minCost: costs.length ? Math.min(...costs) : 0,
            maxCost: costs.length ? Math.max(...costs) : 0,
          });
        }
        // Sort by variant count desc (most-explored families first), then
        // by cost-spread desc as a tiebreaker so dramatic families surface.
        families.sort((a, b) => (b.variantCount - a.variantCount) || ((b.maxCost - b.minCost) - (a.maxCost - a.minCost)));
        if (!cancelled) setVoicedFamilies(families.slice(0, 50));
      } catch {
        if (!cancelled) setVoicedFamilies([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Esc exits expanded mode — mirrors what visitors expect from any modal.
  useEffect(() => {
    if (!isExpanded) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsExpanded(false); };
    document.addEventListener('keydown', handler);
    // Prevent background scroll while the takeover is active.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Signal the global PatrioticScrollbar to hide itself — the iframe
    // takeover covers the viewport, so the scrollbar gutter is just visual
    // noise sitting on top of the iframe with nothing to scroll.
    document.body.setAttribute('data-explorer-expanded', 'true');
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = prevOverflow;
      document.body.removeAttribute('data-explorer-expanded');
    };
  }, [isExpanded]);

  // Post the current dimension to the iframe. Scenario steps post a
  // `demo:scenario` payload (which navigates the iframe into scenario mode
  // for that uuid); column/metric steps post `demo:open` (which expands the
  // matching column group + sorts). Bails if the iframe hasn't loaded yet
  // — the load handler re-posts the current step once it does.
  const postDimension = useCallback((idx: number) => {
    const dim = DEMO_DIMENSIONS[idx];
    if (!dim) return;
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    try {
      if (dim.group === "scenarios" && dim.scenarioId) {
        win.postMessage({ type: "demo:scenario", scenario: dim.scenarioId }, "*");
      } else {
        win.postMessage({ type: "demo:open", dimension: dim.key }, "*");
      }
    } catch {
      // Cross-origin postMessage can throw if the iframe navigated away;
      // we swallow because the next cycle tick will re-post.
    }
  }, []);

  // Re-post whenever activeIdx changes (and the iframe is ready).
  useEffect(() => {
    if (iframeLoaded) postDimension(activeIdx);
  }, [activeIdx, iframeLoaded, postDimension]);

  // Receive `demo:selected` back from the iframe — fires whenever the
  // iframe's active lens changes (either from our own demo:open / demo:scenario
  // round-trip, or because the user clicked something inside the iframe in
  // expanded mode). Keeps the left-rail highlight in sync so the visitor
  // can see what they're actually looking at.
  useEffect(() => {
    const handler = (e: MessageEvent) => {
      const iframeWin = iframeRef.current?.contentWindow;
      // Only trust messages from our own iframe — avoids reacting to stray
      // postMessage chatter from extensions or other embeds on the page.
      if (!iframeWin || e.source !== iframeWin) return;
      const msg = e.data;
      if (!msg || typeof msg !== "object" || msg.type !== "demo:selected") return;
      let targetIdx = -1;
      if (typeof msg.scenario === "string") {
        targetIdx = DEMO_DIMENSIONS.findIndex((d) => d.scenarioId === msg.scenario);
      } else if (typeof msg.dimension === "string") {
        targetIdx = DEMO_DIMENSIONS.findIndex((d) => d.key === msg.dimension);
      }
      if (targetIdx >= 0) {
        setActiveIdx((prev) => (prev === targetIdx ? prev : targetIdx));
        // A user-initiated change inside the iframe should lock auto-cycle —
        // otherwise the tour would wander off the visitor's pick after 6.5s.
        setUserLocked(true);
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  // Auto-cycle — runs only when the user hasn't locked + the iframe loaded
  // + the visitor isn't hovering. Pauses are cumulative; any one false
  // condition stops the timer.
  useEffect(() => {
    if (userLocked || paused || !iframeLoaded) return;
    const id = setInterval(() => {
      setActiveIdx((i) => (i + 1) % DEMO_DIMENSIONS.length);
    }, DEMO_CYCLE_MS);
    return () => clearInterval(id);
  }, [userLocked, paused, iframeLoaded]);

  const pickDimension = (idx: number) => {
    setActiveIdx(idx);
    setUserLocked(true);
  };

  return (
    <div
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className="mt-10 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-0 items-stretch"
      style={{
        background: T.cardBg,
        border: `1px solid ${T.cardBorder}`,
        borderRadius: 10,
        overflow: "hidden",
        boxShadow: `0 24px 64px ${isDark ? "rgba(0,0,0,0.5)" : "rgba(0,40,104,0.08)"}`,
      }}
    >
      {/* ── Left rail: dimension guide ─────────────────────────────────── */}
      <div
        style={{
          borderRight: `1px solid ${T.cardBorder}`,
          padding: "20px 0",
          overflowY: "auto",
        }}
      >
        <div style={{ padding: "0 20px 12px", fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: T.gold }}>
          {t('homepage:homeBeta.tool.tour.header', { count: DEMO_DIMENSIONS.length })}
        </div>
        <div style={{ padding: "0 20px 14px", fontSize: 12, color: T.textMuted, lineHeight: 1.5 }}>
          {userLocked ? t('homepage:homeBeta.tool.tour.locked') : t('homepage:homeBeta.tool.tour.cycling')}
        </div>
        {DEMO_DIMENSIONS.map((dim, idx) => {
          const meta = DEMO_GROUP_META[dim.group];
          const isActive = idx === activeIdx;
          const prevGroup = idx > 0 ? DEMO_DIMENSIONS[idx - 1].group : null;
          const nextGroup = idx + 1 < DEMO_DIMENSIONS.length ? DEMO_DIMENSIONS[idx + 1].group : null;
          const showGroupLabel = dim.group !== prevGroup;
          // Append the paginated voiced-families block right after the last
          // scenarios anchor — gives the demoted Scenarios group a "see all"
          // surface without a separate component.
          const isLastInScenariosGroup = dim.group === "scenarios" && nextGroup !== "scenarios";
          return (
            <div key={dim.key}>
              {showGroupLabel && (
                <div
                  className="flex items-center gap-2"
                  style={{
                    padding: "12px 20px 4px",
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: meta.color,
                  }}
                >
                  <span>{t(`homepage:homeBeta.tool.groups.${dim.group}`, meta.label)}</span>
                  {meta.badge && (
                    <span
                      style={{
                        fontSize: 8,
                        fontWeight: 800,
                        letterSpacing: "0.16em",
                        padding: "2px 6px",
                        borderRadius: 3,
                        background: `${meta.color}1a`,
                        border: `1px solid ${meta.color}55`,
                        color: meta.color,
                      }}
                    >
                      ★ {meta.badge}
                    </span>
                  )}
                </div>
              )}
              <button
                type="button"
                onClick={() => pickDimension(idx)}
                aria-current={isActive ? "true" : undefined}
                style={{
                  width: "100%",
                  textAlign: "left",
                  padding: "10px 20px 10px 24px",
                  background: isActive ? `${meta.color}14` : "transparent",
                  borderLeft: `3px solid ${isActive ? meta.color : "transparent"}`,
                  cursor: "pointer",
                  fontFamily: sans,
                  fontSize: 14,
                  fontWeight: isActive ? 700 : 500,
                  color: isActive ? T.text : T.text,
                  transition: "background 200ms ease, border-color 200ms ease",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  border: 0,
                  borderBottom: 0,
                }}
              >
                {/* Per-dimension label looked up by stable key; falls back to
                    the hardcoded English label if no translation lands yet. */}
                <span style={{ flex: 1 }}>{t(`homepage:homeBeta.tool.dimensions.${dim.key}.label`, dim.label)}</span>
                {dim.comingSoon ? (
                  <span
                    style={{
                      fontSize: 8,
                      fontWeight: 800,
                      letterSpacing: "0.12em",
                      padding: "1px 5px",
                      borderRadius: 3,
                      background: "rgba(180,180,180,0.15)",
                      border: "1px solid rgba(180,180,180,0.35)",
                      color: T.textMuted,
                      flexShrink: 0,
                    }}
                  >
                    {t('homepage:homeBeta.tool.comingSoon', { defaultValue: 'COMING SOON' })}
                  </span>
                ) : dim.isNew ? (
                  <span
                    style={{
                      fontSize: 8,
                      fontWeight: 800,
                      letterSpacing: "0.12em",
                      padding: "1px 5px",
                      borderRadius: 3,
                      background: `${meta.color}1a`,
                      color: meta.color,
                      flexShrink: 0,
                    }}
                  >
                    NEW
                  </span>
                ) : null}
                {isActive && <ArrowRight className="w-3.5 h-3.5" style={{ color: meta.color }} />}
              </button>
              {isActive && (
                <div style={{ padding: "0 20px 8px 24px", fontSize: 11.5, color: T.textMuted, lineHeight: 1.55 }}>
                  {t(`homepage:homeBeta.tool.dimensions.${dim.key}.hint`, dim.hint)}
                </div>
              )}
              {/* Paginated voiced-families — appended once after the last
               *  scenarios anchor. Loads ~50 families on mount; pages of 7. */}
              {isLastInScenariosGroup && (() => {
                const scenariosMeta = DEMO_GROUP_META.scenarios;
                if (voicedFamilies === null) {
                  return (
                    <div style={{ padding: "8px 20px", fontSize: 11, color: T.textMuted, fontStyle: "italic" }}>
                      {t('homepage:homeBeta.tool.scenarios.loading', { defaultValue: 'Loading voiced scenarios…' })}
                    </div>
                  );
                }
                if (voicedFamilies.length === 0) return null;
                const totalPages = Math.max(1, Math.ceil(voicedFamilies.length / SCENARIOS_PAGE_SIZE));
                const start = scenarioPage * SCENARIOS_PAGE_SIZE;
                const visible = voicedFamilies.slice(start, start + SCENARIOS_PAGE_SIZE);
                const fmt = (n: number) =>
                  n >= 1000 ? `$${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : `$${Math.round(n).toLocaleString()}`;
                return (
                  <div style={{ marginTop: 4 }}>
                    <div style={{ padding: "6px 20px 4px", fontSize: 9, color: T.textMuted, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                      {t('homepage:homeBeta.tool.scenarios.voicedHeader', { defaultValue: '↳ Voiced families' })}
                    </div>
                    {visible.map((f) => (
                      <Link
                        key={f.familyId}
                        to={`/scenarios/${f.baseUuid}/${f.baseSlug}`}
                        className="flex items-center gap-2 transition-colors"
                        style={{
                          padding: "6px 20px 6px 28px",
                          color: T.text,
                          textDecoration: "none",
                          fontSize: 12,
                          borderLeft: `3px solid transparent`,
                        }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = `${scenariosMeta.color}10`; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = "transparent"; }}
                      >
                        <span className="flex-1 min-w-0 truncate">{f.baseName}</span>
                        <span style={{ fontSize: 9, fontWeight: 600, color: scenariosMeta.color, opacity: 0.8 }}>
                          {f.variantCount}v
                        </span>
                        {f.minCost > 0 && f.maxCost > 0 && (
                          <span style={{ fontFamily: "ui-monospace, monospace", fontSize: 10, color: T.textMuted, flexShrink: 0 }}>
                            {f.minCost === f.maxCost ? fmt(f.minCost) : `${fmt(f.minCost)}–${fmt(f.maxCost)}`}
                          </span>
                        )}
                      </Link>
                    ))}
                    {/* Pagination footer — ← N of M → + browse-all link */}
                    <div className="flex items-center justify-between" style={{ padding: "8px 20px 4px" }}>
                      <button
                        type="button"
                        onClick={() => setScenarioPage((p) => Math.max(0, p - 1))}
                        disabled={scenarioPage === 0}
                        aria-label={t('homepage:homeBeta.tool.scenarios.prevPage', { defaultValue: 'Previous page' }) as string}
                        style={{
                          background: "transparent",
                          border: `1px solid ${T.cardBorder}`,
                          borderRadius: 4,
                          padding: "2px 6px",
                          color: T.text,
                          fontSize: 11,
                          opacity: scenarioPage === 0 ? 0.35 : 1,
                          cursor: scenarioPage === 0 ? "not-allowed" : "pointer",
                        }}
                      >
                        <ChevronLeft className="w-3 h-3" />
                      </button>
                      <span style={{ fontSize: 10, color: T.textMuted, fontVariantNumeric: "tabular-nums" }}>
                        {t('homepage:homeBeta.tool.scenarios.pageLabel', {
                          current: scenarioPage + 1,
                          total: totalPages,
                          defaultValue: 'Page {{current}} of {{total}}',
                        })}
                      </span>
                      <button
                        type="button"
                        onClick={() => setScenarioPage((p) => Math.min(totalPages - 1, p + 1))}
                        disabled={scenarioPage >= totalPages - 1}
                        aria-label={t('homepage:homeBeta.tool.scenarios.nextPage', { defaultValue: 'Next page' }) as string}
                        style={{
                          background: "transparent",
                          border: `1px solid ${T.cardBorder}`,
                          borderRadius: 4,
                          padding: "2px 6px",
                          color: T.text,
                          fontSize: 11,
                          opacity: scenarioPage >= totalPages - 1 ? 0.35 : 1,
                          cursor: scenarioPage >= totalPages - 1 ? "not-allowed" : "pointer",
                        }}
                      >
                        <ChevronRight className="w-3 h-3" />
                      </button>
                    </div>
                    <Link
                      to="/scenarios"
                      className="inline-flex items-center gap-1"
                      style={{
                        padding: "2px 20px 8px",
                        color: scenariosMeta.color,
                        fontSize: 11,
                        fontWeight: 600,
                        textDecoration: "none",
                      }}
                    >
                      {t('homepage:homeBeta.tool.scenarios.browseAll', { defaultValue: 'Browse all scenarios →' })}
                    </Link>
                  </div>
                );
              })()}
            </div>
          );
        })}
        <div style={{ padding: "16px 20px 4px", borderTop: `1px solid ${T.cardBorder}`, marginTop: 16 }}>
          <Link
            to="/hospital-rankings"
            className="inline-flex items-center gap-1.5"
            style={{
              color: T.gold,
              fontSize: 13,
              fontWeight: 600,
              textDecoration: "none",
            }}
          >
            {t('homepage:homeBeta.tool.tour.openFull')} <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>

      {/* ── Right: live iframe preview ─────────────────────────────────── */}
      {/* Column stretches to match the left rail's height via the grid's
          align-items: stretch. The iframe fills the remaining space below
          the browser-chrome strip, so no dead black gap appears below the
          rankings table content. */}
      <div style={{ position: "relative", background: "#0a1628", display: "flex", flexDirection: "column" }}>
        <div
          className="flex items-center gap-3 px-4 py-2"
          style={{
            borderBottom: `1px solid ${T.cardBorder}`,
            fontSize: 12,
            color: T.textMuted,
            background: isDark ? "rgba(255,255,255,0.02)" : "rgba(27,27,27,0.025)",
            flexShrink: 0,
          }}
        >
          <div className="flex items-center gap-1.5">
            <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#ff5f57" }} />
            <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#febc2e" }} />
            <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#28c840" }} />
          </div>
          <span style={{ fontFamily: mono, fontSize: 11 }}>
            americanhospitalprices.com/hospital-rankings
          </span>
          <span className="ml-auto hidden sm:flex" style={{ fontSize: 11, color: T.gold, fontWeight: 600 }}>
            {t('homepage:homeBeta.tool.tour.liveBadge', { defaultValue: 'Live ·' })} {DEMO_DIMENSIONS[activeIdx] ? t(`homepage:homeBeta.tool.dimensions.${DEMO_DIMENSIONS[activeIdx].key}.label`, DEMO_DIMENSIONS[activeIdx].label) : ''}
          </span>
        </div>
        <div
          className="group"
          style={
            isExpanded
              ? {
                  // In-page takeover: position-fixed to cover the viewport,
                  // staying inside the current tab. Browser chrome (tabs,
                  // URL bar, dock) remains visible.
                  position: "fixed",
                  inset: 0,
                  zIndex: 9998,
                  background: "#0a1628",
                  overflow: "hidden",
                }
              : {
                  position: "relative",
                  flex: "1 1 auto",
                  minHeight: 480,
                  overflow: "hidden",
                  background: "#0a1628",
                }
          }
        >
          <iframe
            ref={iframeRef}
            src="/hospital-rankings?embed=1"
            title={t('homepage:homeBeta.tool.tour.iframeTitle')}
            loading="lazy"
            onLoad={() => setIframeLoaded(true)}
            // Expanded mode renders the page at native resolution (no scale,
            // no pointer-events block). Returns to the scaled, click-through
            // preview when the visitor Escapes or clicks the close button.
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: isExpanded ? "100%" : "153.85%",
              height: isExpanded ? "100%" : "153.85%",
              border: 0,
              transform: isExpanded ? "none" : "scale(0.65)",
              transformOrigin: "top left",
              pointerEvents: isExpanded ? "auto" : "none",
              background: "#0a1628",
            }}
            sandbox="allow-scripts allow-same-origin"
            referrerPolicy="same-origin"
          />
          {/* Hover overlay — vignette + "expand" CTA. Iframe is
              pointer-events: none in preview mode so clicks would do nothing
              and feel broken; this overlay signals the preview state and
              gives the visitor a clear path to actually USE the tool. */}
          {!isExpanded && (
            <div
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none group-hover:pointer-events-auto"
              style={{
                background:
                  "radial-gradient(ellipse at center, rgba(0,0,0,0) 30%, rgba(0,40,104,0.55) 75%, rgba(0,20,60,0.85) 100%)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
              }}
            >
              <button
                type="button"
                onClick={() => setIsExpanded(true)}
                className="inline-flex items-center gap-2 transition-transform hover:translate-x-0.5"
                style={{
                  background: T.gold,
                  color: "#0a1628",
                  padding: "12px 22px",
                  borderRadius: 6,
                  border: 0,
                  fontFamily: sans,
                  fontSize: 14,
                  fontWeight: 700,
                  letterSpacing: "0.02em",
                  cursor: "pointer",
                  boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
                }}
              >
                <Maximize2 className="w-4 h-4" /> {t('homepage:homeBeta.tool.tour.expand')}
              </button>
              <a
                href="/hospital-rankings"
                target="_blank"
                rel="noopener"
                style={{
                  fontFamily: sans,
                  fontSize: 12,
                  color: "rgba(255,255,255,0.8)",
                  textDecoration: "underline",
                  textUnderlineOffset: 3,
                }}
              >
                {t('homepage:homeBeta.tool.tour.openNewTab')}
              </a>
            </div>
          )}
          {/* Close button — only when expanded. Top-right, sits over the
              iframe at high z-index so the visitor can dismiss the takeover
              without hunting for the Esc key. */}
          {isExpanded && (
            <button
              type="button"
              onClick={() => setIsExpanded(false)}
              aria-label={t('homepage:homeBeta.tool.tour.closeAria')}
              style={{
                position: "absolute",
                top: 16,
                right: 16,
                zIndex: 10000,
                background: "rgba(10,22,40,0.85)",
                color: "#FAF9F6",
                border: `1px solid rgba(250,249,246,0.2)`,
                borderRadius: 999,
                padding: "8px 14px",
                fontFamily: sans,
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
                boxShadow: "0 6px 20px rgba(0,0,0,0.4)",
              }}
            >
              {t('homepage:homeBeta.tool.tour.close')} <span style={{ fontFamily: mono, fontSize: 11, opacity: 0.7 }}>(Esc)</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// mono font reused by the demo header chrome.
const mono = "ui-monospace, SFMono-Regular, Menlo, monospace";

function FeatureLine({
  T, icon, title, body,
}: {
  T: ReturnType<typeof useThemeTokens>;
  icon: React.ReactNode; title: string; body: string;
}) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2" style={{ color: T.gold }}>
        {icon}
        <span style={{ fontWeight: 600, fontSize: "0.95rem", color: T.text }}>{title}</span>
      </div>
      <p style={{ color: T.textMuted, fontSize: "0.92rem", lineHeight: 1.5 }}>{body}</p>
    </div>
  );
}

// Featured families for the "family of outcomes" homepage section. Hand-picked
// to mix recognizable, diverse diagnoses (maternity, cardiac emergency, ortho
// emergency, common abdominal surgery, elective ortho, GI emergency). Each
// base UUID resolves to a sev-0 scenario whose family has variants across
// multiple severity levels in the LIVE scenarios_index.json (verified at
// curation time). New entries should be checked against the index — if the
// base UUID isn't there, the showcase renders a skeleton tile for that slot.
const FEATURED_FAMILY_BASE_UUIDS: Array<{ uuid: string; tag: string }> = [
  { uuid: "550e8400-e29b-41d4-a716-446655440002", tag: "Maternity" },         // C-Section Delivery — 28 variants
  { uuid: "3f0682b3-4dfc-42ca-8bce-5492280bc399", tag: "Common surgery" },    // Gallbladder Removal — 15 variants
  { uuid: "57a67b9e-1ead-466b-b9ba-23b303485de2", tag: "Ortho emergency" },   // Broken Hip Repair (ORIF) — 5 variants
  { uuid: "550e8400-e29b-41d4-a716-446655440003", tag: "Cardiac emergency" }, // Heart Attack Treatment — 5 variants
];

// Severity here is COST severity — quartiles of the family's price spread,
// computed at refresh time — NOT clinical urgency. The old MINOR/MODERATE/
// SERIOUS/CRITICAL labels implied a clinical reading we can't defend (the
// pipeline doesn't classify clinical risk; it sorts by chargemaster cost).
// Cost-quartile labels make the metaphor honest and match the dollar ranges
// already shown next to the pills.
const SEV_LABELS = ["Base", "Low-cost", "Mid-cost", "High-cost", "Top-cost"];
const SEV_COLORS = ["#2b5e49", "#5a9a3c", "#C5A55A", "#e67e22", "#B22234"];

/**
 * DemoFamilyStack — replaces the old "Five Outcomes" hardcoded ladder.
 * Pulls a real family (default: Uncomplicated Vaginal Delivery, 32 variants)
 * from rowData, groups its members by computed severity, and renders one
 * stacked-card column per active severity level. Mirrors the visual model
 * used at the top of /scenarios/:uuid/ so the metaphor reads consistently
 * across surfaces. Each column opens the ScenarioFamilyModal pre-scrolled
 * to that severity bucket — so users can walk into the actual variants.
 */
/**
 * FeaturedFamilyShowcase — replaces the old "Five Outcomes" hardcoded ladder.
 * Renders a curated row of hand-picked family scenarios. Each tile is a real
 * ScenarioCard so the look matches what consumers see elsewhere on the site,
 * wrapped in StackedFamilyCard which adds shadow-layer cards behind the real
 * card scaled to variant depth — a 28-variant family visibly looks like a
 * tall stack vs. a 5-variant family which gets a lighter stack hint.
 *
 * Card interactivity (hover any severity pill → variant peek; click → family
 * explorer) is already wired inside ScenarioCard itself, so the section gets
 * the full explorer experience for free.
 */
function FeaturedFamilyShowcase({
  T,
  rowData,
  bookmarks,
  onToggleBookmark,
  cardTheme,
}: {
  T: ReturnType<typeof useThemeTokens>;
  rowData: ScenarioRowData | null;
  bookmarks: Set<string>;
  onToggleBookmark: (id: string) => void;
  cardTheme: CardTheme;
}) {
  return (
    <div className="mt-10 space-y-12">
      {FEATURED_FAMILY_BASE_UUIDS.map(({ uuid, tag }) => (
        <FamilyShowcaseRow
          key={uuid}
          T={T}
          baseUuid={uuid}
          tag={tag}
          rowData={rowData}
          bookmarks={bookmarks}
          onToggleBookmark={onToggleBookmark}
          cardTheme={cardTheme}
        />
      ))}
    </div>
  );
}

/**
 * FamilyShowcaseRow — one featured family rendered as a 2-column layout:
 *   • Left: the stacked ScenarioCard (with shadow-card layers behind it)
 *   • Right: an inline, embedded view of the family's variants grouped by
 *            severity bucket — the same structure ScenarioFamilyModal shows
 *            on click, surfaced inline so consumers can immediately see how
 *            variants stack at each level without opening a modal.
 * The right panel still includes a "Open full explorer" link that triggers
 * the modal for the deeper variant-card view with phase bars.
 */
function FamilyShowcaseRow({
  T,
  baseUuid,
  tag,
  rowData,
  bookmarks,
  onToggleBookmark,
  cardTheme,
}: {
  T: ReturnType<typeof useThemeTokens>;
  baseUuid: string;
  tag: string;
  rowData: ScenarioRowData | null;
  bookmarks: Set<string>;
  onToggleBookmark: (id: string) => void;
  cardTheme: CardTheme;
}) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8 items-start">
      {/* Left: stacked card. Sticky on md+ so it stays anchored while the
          right panel's variant list scrolls past — gives the section a
          "card-and-spec-sheet" feel rather than two unrelated columns. */}
      <div className="md:col-span-5 lg:col-span-4">
        <div className="md:sticky md:top-6">
          <StackedFamilyCard
            T={T}
            baseUuid={baseUuid}
            tag={tag}
            rowData={rowData}
            bookmarks={bookmarks}
            onToggleBookmark={onToggleBookmark}
            cardTheme={cardTheme}
          />
        </div>
      </div>
      {/* Right: inline severity-grouped variant list. */}
      <div className="md:col-span-7 lg:col-span-8">
        <InlineFamilyVariantsList T={T} baseUuid={baseUuid} rowData={rowData} cardTheme={cardTheme} />
      </div>
    </div>
  );
}

/**
 * StackedFamilyCard — wraps a ScenarioCard with shadow-card layers behind it,
 * scaled to family variant depth. The shadow layers use the same card border
 * and background so they read as "more cards just like this one stacked behind."
 * Depth caps at 3 visible layers so tower-tall families don't dominate the row.
 */
function StackedFamilyCard({
  T,
  baseUuid,
  tag,
  rowData,
  bookmarks,
  onToggleBookmark,
  cardTheme,
}: {
  T: ReturnType<typeof useThemeTokens>;
  baseUuid: string;
  tag: string;
  rowData: ScenarioRowData | null;
  bookmarks: Set<string>;
  onToggleBookmark: (id: string) => void;
  cardTheme: CardTheme;
}) {
  const { isDark } = useTheme();
  const card = rowData?.byUuid?.get(baseUuid);
  const siblings = rowData?.siblingMap?.get(baseUuid) ?? [];

  // Skeleton placeholder while the index loads (keeps the row footprint stable
  // so the page doesn't shift in when data arrives).
  if (!card) {
    return (
      <div style={{ minHeight: 380, position: "relative" }}>
        <div
          style={{
            background: T.cardBg,
            border: `1px solid ${T.cardBorder}`,
            borderRadius: 8,
            opacity: 0.4,
            height: "100%",
            minHeight: 380,
          }}
        />
      </div>
    );
  }

  // Stack depth: 1 layer for 2-4 variants, 2 for 5-9, 3 for 10+. Caps so a
  // 28-variant family doesn't sprout an absurdly tall tower.
  const variantCount = siblings.length;
  const stackDepth = variantCount >= 10 ? 3 : variantCount >= 5 ? 2 : variantCount >= 2 ? 1 : 0;

  // How wide the family stack actually spans across severities — surfaces
  // "covers all 5 buckets" type tags above the card.
  const distinctSevs = new Set<number>([Math.max(0, Math.min(4, Math.round(card.severity_level ?? 0)))]);
  siblings.forEach((s) => distinctSevs.add(Math.max(0, Math.min(4, Math.round(s.severity_level ?? 0)))));

  return (
    <div style={{ position: "relative" }}>
      {/* Tag chip + variant-count pill above the card so the showcase reads
          as a curated grid (without modifying ScenarioCard itself). Sits
          OUTSIDE the stack-shadow container so the depth layers never extend
          up into the badge row. */}
      <div className="flex items-center justify-between mb-3">
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.5px",
            textTransform: "uppercase",
            color: T.gold,
            background: "rgba(197,165,90,0.12)",
            border: "1px solid rgba(197,165,90,0.30)",
            padding: "3px 8px",
            borderRadius: 3,
          }}
        >
          {tag}
        </span>
        {variantCount > 0 && (
          <span
            className="inline-flex items-center gap-1"
            style={{
              fontSize: 10,
              fontWeight: 600,
              color: T.textMuted,
            }}
          >
            <Layers className="w-3 h-3" style={{ opacity: 0.7 }} />
            {variantCount + 1} variants · {distinctSevs.size} severity {distinctSevs.size === 1 ? "level" : "levels"}
          </span>
        )}
      </div>

      {/* Card + stack-shadow wrapper. Bounded so shadow layers extend below+
          right of the card only, never up into the badge area above. */}
      <div style={{ position: "relative", paddingRight: stackDepth * 6, paddingBottom: stackDepth * 6 }}>
        {/* Stack shadow layers. Offset bottom-right and progressively dimmer
            so the stack reads as depth, not a duplicate card.
            Dark mode previously used a translucent CREAM tint — which read as a
            halo/glow around the main card rather than stacked paper. Now we
            render solid slightly-lighter-than-bg navy cards with a very faint
            border, giving a real "paper stack" feel. Light mode is unchanged. */}
        {[3, 2, 1].map((depth) => {
          if (stackDepth < depth) return null;
          const offset = depth * 6;
          // Light mode: keep prior translucency for the cream-on-cream stack tell.
          // Dark mode: full opacity, solid navy tint per depth — looks like
          // discrete paper rather than haloed glow.
          const opacity = isDark ? 1 : 0.18 + 0.12 * (4 - depth);
          // Dark-mode solid navy tints, each depth slightly cooler/lighter than
          // the next so they read as recede-into-the-stack. Hand-picked against
          // the page bg (#050e1a) and card bg (#0d1e35).
          const darkBgByDepth: Record<number, string> = { 1: "#142a45", 2: "#10243c", 3: "#0c1f33" };
          const shadowBg = isDark ? darkBgByDepth[depth] : T.cardBg;
          const shadowBorder = isDark ? "rgba(250,249,246,0.05)" : T.cardBorder;
          return (
            <div
              key={depth}
              aria-hidden
              style={{
                position: "absolute",
                top: offset,
                left: offset,
                right: -offset,
                bottom: -offset,
                background: shadowBg,
                border: `1px solid ${shadowBorder}`,
                borderRadius: 8,
                opacity,
                zIndex: 0,
                pointerEvents: "none",
              }}
            />
          );
        })}

        {/* The real ScenarioCard — its own bottom bar handles per-pill hover
            peek + click-to-open-family-explorer, so the user can drive into
            the variants without leaving this section. */}
        <div style={{ position: "relative", zIndex: 1 }}>
          <ScenarioCard
            scenario={card}
            compact
            isStarred={bookmarks.has(card.id)}
            theme={cardTheme}
            onToggleBookmark={onToggleBookmark}
            familySiblings={siblings}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * InlineFamilyVariantsList — embedded version of the ScenarioFamilyModal body.
 * Renders the same severity-bucket grouping the modal uses (cost-range chips
 * across the top, per-level variant rows below), but inline on the page so a
 * consumer can see what's in each bucket without opening the modal. Clicking
 * any row still navigates into that specific variant's scenario page; the
 * "Open full explorer" link mounts the same modal for the richer card view
 * (phase bars, full descriptions, etc.).
 *
 * Quartile boundaries are computed the same way ScenarioFamilyModal does, so
 * the inline view and the modal's view of the same family stay consistent.
 */
function InlineFamilyVariantsList({
  T,
  baseUuid,
  rowData,
  cardTheme,
}: {
  T: ReturnType<typeof useThemeTokens>;
  baseUuid: string;
  rowData: ScenarioRowData | null;
  cardTheme: CardTheme;
}) {
  const { t } = useTranslation(['homepage']);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLevel, setModalLevel] = useState<number | null>(null);

  const base = rowData?.byUuid?.get(baseUuid);
  const siblings = rowData?.siblingMap?.get(baseUuid) ?? [];

  // Group base + siblings by quartile-computed severity. Mirrors the logic
  // inside ScenarioFamilyModal so the inline view and modal agree on buckets.
  const { byLevel, baseCost, levelRanges } = useMemo(() => {
    if (!base) return { byLevel: {} as Record<number, Array<{ id: string; name: string; cost: number; isBase: boolean }>>, baseCost: 0, levelRanges: [] as Array<{ min: number; max: number }> };
    type Entry = { id: string; name: string; cost: number; isBase: boolean; rawSev: number };
    const members: Entry[] = [
      { id: base.id, name: base.name, cost: base.calculated_avg_cost ?? 0, isBase: true, rawSev: base.severity_level ?? 0 },
      ...siblings.map((s) => ({
        id: s.id,
        name: s.name,
        cost: s.estimatedCost ?? 0,
        isBase: false,
        rawSev: s.severity_level ?? 0,
      })),
    ];
    const baseIds = new Set(members.filter((m) => m.rawSev === 0).map((m) => m.id));
    const groups: Record<number, Array<{ id: string; name: string; cost: number; isBase: boolean }>> = { 0: [], 1: [], 2: [], 3: [], 4: [] };
    members.filter((m) => baseIds.has(m.id)).forEach((m) => groups[0].push({ id: m.id, name: m.name, cost: m.cost, isBase: m.isBase }));
    const nonBase = members.filter((m) => !baseIds.has(m.id)).sort((a, b) => a.cost - b.cost);
    const n = nonBase.length;
    nonBase.forEach((m, idx) => {
      const pct = n <= 1 ? 1 : idx / (n - 1);
      const lv = pct <= 0.25 ? 1 : pct <= 0.5 ? 2 : pct <= 0.75 ? 3 : 4;
      groups[lv].push({ id: m.id, name: m.name, cost: m.cost, isBase: false });
    });
    Object.values(groups).forEach((arr) => arr.sort((a, b) => a.cost - b.cost));
    const bc = groups[0][0]?.cost ?? 0;
    const ranges = [0, 1, 2, 3, 4].map((lv) => {
      const costs = groups[lv].map((m) => m.cost).filter((c) => c > 0);
      return { min: costs.length ? Math.min(...costs) : 0, max: costs.length ? Math.max(...costs) : 0 };
    });
    return { byLevel: groups, baseCost: bc, levelRanges: ranges };
  }, [base, siblings]);

  const familySyntheticId = `inline-family-${baseUuid}`;
  const modalScenarios: FamilyModalScenario[] = useMemo(() => {
    if (!base) return [];
    return [
      {
        id: base.id,
        name: base.name,
        description: base.description,
        icon_name: base.icon_name,
        color: base.color,
        severityLevel: base.severity_level ?? 0,
        scenario_family_id: familySyntheticId,
        estimatedCost: base.calculated_avg_cost ?? 0,
        // Same reasoning as the siblings branch — modal renders procs from
        // s.totalProcedures, so the base row needs it explicitly too or the
        // base variant shows "0 procedures" while siblings show their counts.
        totalProcedures: base.total_procedures ?? 0,
        phaseBreakdown: base.phaseBreakdown,
      } as FamilyModalScenario,
      ...siblings.map((s) => ({
        id: s.id,
        name: s.name,
        severityLevel: s.severity_level ?? 0,
        scenario_family_id: familySyntheticId,
        estimatedCost: s.estimatedCost ?? 0,
        // Without this the modal falls back to (s.totalProcedures ?? 0) and
        // every variant row renders "0 procedures" regardless of how many
        // procedures the variant actually has in scenario_procedures.
        totalProcedures: s.totalProcedures ?? 0,
      } as FamilyModalScenario)),
    ];
  }, [base, siblings, familySyntheticId]);

  const fmt = (n: number) =>
    n >= 1000 ? `$${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : `$${Math.round(n).toLocaleString()}`;

  const openAt = (lv: number | null) => {
    setModalLevel(lv);
    setModalOpen(true);
  };

  if (!base) {
    return (
      <div
        style={{
          background: T.cardBg,
          border: `1px solid ${T.cardBorder}`,
          borderRadius: 8,
          padding: 24,
          opacity: 0.5,
          minHeight: 280,
        }}
      />
    );
  }

  return (
    <div
      style={{
        background: T.cardBg,
        border: `1px solid ${T.cardBorder}`,
        borderRadius: 8,
        overflow: "hidden",
      }}
    >
      {/* Header — name + base cost + variant count + open-explorer link */}
      <div className="px-5 py-4" style={{ borderBottom: `1px solid ${T.cardBorder}` }}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: T.gold, letterSpacing: "0.5px", textTransform: "uppercase" }}>
              {t('homepage:homeBeta.familyModal.variantsInFamily')}
            </div>
            <div style={{ fontFamily: serif, fontSize: "1.15rem", color: T.text, marginTop: 2, lineHeight: 1.2 }}>
              {base.name}
            </div>
            <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>
              {/* Trans component lets translators move the {{cost}} highlight
                  into the correct word order without breaking the JSX span. */}
              <Trans
                i18nKey="homepage:homeBeta.familyModal.headerSummary"
                values={{ cost: fmt(baseCost), count: siblings.length, noun: siblings.length === 1 ? t('homepage:homeBeta.familyModal.variantSingular') : t('homepage:homeBeta.familyModal.variantPlural') }}
                components={{ baseCost: <span style={{ color: SEV_COLORS[0], fontWeight: 700 }} /> }}
              />
            </div>
          </div>
          <button
            type="button"
            onClick={() => openAt(null)}
            className="hover:opacity-80 transition-opacity flex-shrink-0"
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: T.blue,
              background: "transparent",
              border: `1px solid ${T.blue}`,
              padding: "6px 12px",
              borderRadius: 4,
              cursor: "pointer",
            }}
          >
            {t('homepage:homeBeta.familyModal.openExplorer')}
          </button>
        </div>
      </div>

      {/* Cost-range chip strip across the 5 severity levels */}
      <div className="flex gap-1 px-5 py-3" style={{ borderBottom: `1px solid ${T.cardBorder}` }}>
        {[0, 1, 2, 3, 4].map((lv) => {
          const count = byLevel[lv].length;
          const color = SEV_COLORS[lv];
          const range = levelRanges[lv];
          return (
            <button
              key={lv}
              type="button"
              onClick={() => count > 0 && openAt(lv)}
              disabled={count === 0}
              className={count > 0 ? "transition-transform hover:-translate-y-0.5" : ""}
              style={{
                flex: 1,
                background: count > 0 ? `${color}10` : "rgba(128,128,128,0.04)",
                border: `1px solid ${count > 0 ? `${color}33` : "rgba(128,128,128,0.12)"}`,
                borderRadius: 4,
                padding: "5px 6px",
                textAlign: "left",
                cursor: count > 0 ? "pointer" : "not-allowed",
                opacity: count > 0 ? 1 : 0.5,
              }}
            >
              <div className="flex items-center gap-1">
                <span
                  className="inline-flex items-center justify-center text-[9px] font-bold flex-shrink-0"
                  style={{ width: 13, height: 13, borderRadius: "50%", backgroundColor: count > 0 ? color : "#cbd5e1", color: "#fff" }}
                >
                  {lv}
                </span>
                <span style={{ fontSize: 9, fontWeight: 600, color: count > 0 ? color : T.textMuted, letterSpacing: 0.3 }}>
                  {count > 0 ? `${count}` : "—"}
                </span>
              </div>
              <div style={{ fontSize: 9, color: count > 0 ? color : T.textMuted, marginTop: 2, fontWeight: 600, fontFamily: "ui-monospace, monospace" }}>
                {count > 0 ? (lv === 0 ? fmt(baseCost) : (range.min === range.max ? fmt(range.min) : `${fmt(range.min)}-${fmt(range.max)}`)) : "—"}
              </div>
            </button>
          );
        })}
      </div>

      {/* Disambiguation microcopy — "severity" here is cost-quartile (price
          spread within the family), not clinical urgency. Without this line,
          the labels read as a medical risk gradient, which is not what the
          pipeline computes. */}
      <div className="px-5 pt-2 pb-1" style={{ fontSize: 10, color: T.textMuted, lineHeight: 1.4, fontStyle: "italic" }}>
        <Trans
          i18nKey="homepage:homeBeta.severityLadder.microcopyRich"
          components={{ b: <span style={{ fontWeight: 600, color: T.text, fontStyle: "normal" }} /> }}
        />
      </div>

      {/* Per-severity-level variant lists. */}
      <div className="px-5 py-3 max-h-[440px] overflow-y-auto">
        {[0, 1, 2, 3, 4].map((lv) => {
          const variants = byLevel[lv];
          if (variants.length === 0) return null;
          const color = SEV_COLORS[lv];
          return (
            <div key={lv} className="mb-3 last:mb-0">
              <div className="flex items-center gap-2 mb-1.5">
                <span
                  className="inline-flex items-center justify-center text-[9px] font-bold"
                  style={{ width: 16, height: 16, borderRadius: "50%", background: color, color: "#fff" }}
                >
                  {lv}
                </span>
                <span style={{ fontSize: 10, fontWeight: 700, color, letterSpacing: 0.4, textTransform: "uppercase" }}>
                  {t(`homepage:homeBeta.severityLadder.${['base','lowCost','midCost','highCost','topCost'][lv]}Upper`)}
                </span>
                {/* For the Base row specifically, append a "(with no
                    complications)" qualifier so the meaning isn't ambiguous —
                    every variant ABOVE this row is something that went wrong,
                    this is the row that says "it didn't." Other levels' meaning
                    is already obvious from their cost-tier labels. */}
                {lv === 0 && (
                  <span style={{ fontSize: 10, fontWeight: 500, color: T.textMuted, fontStyle: "italic" }}>
                    {t('homepage:homeBeta.familyModal.withNoComplications')}
                  </span>
                )}
                <span style={{ fontSize: 10, color: T.textMuted }}>
                  {variants.length} {variants.length === 1 ? t('homepage:homeBeta.familyModal.variantSingular') : t('homepage:homeBeta.familyModal.variantPlural')}
                </span>
                <div className="h-px flex-1" style={{ background: `${color}22` }} />
              </div>
              <ul className="space-y-1">
                {variants.map((v) => {
                  const variantName = v.isBase
                    ? v.name
                    : v.name.replace(`${base.name} — `, "").replace(`${base.name}: `, "").replace(`${base.name} `, "");
                  const delta = v.cost - baseCost;
                  return (
                    <li key={v.id}>
                      <Link
                        to={`/scenarios/${v.id}/${v.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className="flex items-center gap-2 px-2 py-1.5 rounded transition-colors hover:bg-black/[0.03]"
                        style={{ textDecoration: "none", color: T.text }}
                      >
                        <span style={{ color, fontSize: 9, opacity: 0.8 }}>●</span>
                        <span className="flex-1 min-w-0" style={{ fontSize: 12, lineHeight: 1.3 }}>
                          <span className="truncate block">
                            {variantName || v.name}
                            {v.isBase && (
                              <span className="ml-2 text-[9px] font-semibold uppercase" style={{ color: T.textMuted }}>
                                {t('homepage:homeBeta.severityLadder.base').toLowerCase()}
                              </span>
                            )}
                          </span>
                        </span>
                        <span style={{ fontFamily: "ui-monospace, monospace", fontSize: 11, fontWeight: 600, color: v.isBase ? color : T.text, flexShrink: 0 }}>
                          {fmt(v.cost)}
                        </span>
                        {!v.isBase && delta > 0 && (
                          <span style={{ fontFamily: "ui-monospace, monospace", fontSize: 10, color: T.textMuted, flexShrink: 0, width: 56, textAlign: "right" }}>
                            +{fmt(delta)}
                          </span>
                        )}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>

      {/* Family explorer modal — opened by the "Open full explorer" link or
          any cost-chip click. Same modal as everywhere else on the site. */}
      <ScenarioFamilyModal
        scenario={{
          id: base.id,
          name: base.name,
          description: base.description,
          icon_name: base.icon_name,
          color: base.color,
          scenario_family_id: familySyntheticId,
        }}
        allRawScenarios={modalScenarios}
        open={modalOpen}
        onOpenChange={setModalOpen}
        theme={cardTheme}
        defaultLevel={modalLevel}
      />
    </div>
  );
}


function SeverityCard({
  T, level, title, body, costRange,
}: {
  T: ReturnType<typeof useThemeTokens>;
  level: number; title: string; body: string; costRange: string;
}) {
  const dots = [0, 1, 2, 3, 4].map((i) => (
    <span key={i} style={{
      width: 8, height: 8, borderRadius: "50%",
      background: i <= level ? T.gold : "rgba(255,255,255,0.15)",
      display: "inline-block",
    }} />
  ));
  return (
    <div
      style={{
        background: T.cardBgOnInverse,
        border: `1px solid ${T.cardBorderOnInverse}`,
        borderRadius: 6,
        padding: 20,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        minHeight: 240,
      }}
    >
      <div className="flex items-center gap-1.5">
        {dots}
      </div>
      <div style={{ fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.12em", color: T.gold, fontWeight: 700, marginTop: 4 }}>
        Severity {level}
      </div>
      <div style={{ fontFamily: serif, fontSize: "1.35rem", lineHeight: 1.1, color: T.textOnInverse, letterSpacing: "-0.01em" }}>
        {title}
      </div>
      <p style={{ color: T.textMutedOnInverse, fontSize: "0.85rem", lineHeight: 1.45, flex: 1 }}>
        {body}
      </p>
      <div style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: "0.9rem", color: T.gold, fontWeight: 700 }}>
        {costRange}
      </div>
    </div>
  );
}

function AtlasCard({
  T, icon, accent, title, body, linkLabel, linkTo,
}: {
  T: ReturnType<typeof useThemeTokens>;
  icon: React.ReactNode; accent: string;
  title: string; body: string; linkLabel: string; linkTo: string;
}) {
  return (
    <Link
      to={linkTo}
      className="group block transition-transform hover:-translate-y-0.5"
      style={{
        background: T.cardBg,
        border: `1px solid ${T.cardBorder}`,
        borderRadius: 8,
        padding: 28,
        textDecoration: "none",
        color: T.text,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute", top: 0, left: 0, width: 4, height: "100%",
          background: accent,
        }}
      />
      <div className="flex items-center gap-2 mb-4" style={{ color: accent }}>
        {icon}
        <span style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.12em", fontWeight: 700 }}>
          {linkLabel.split(" ").slice(0, 2).join(" ")}
        </span>
      </div>
      <h3 style={{ fontFamily: serif, fontSize: "1.5rem", lineHeight: 1.15, letterSpacing: "-0.01em", color: T.text, margin: 0 }}>
        {title}
      </h3>
      <p style={{ color: T.textMuted, fontSize: "0.95rem", lineHeight: 1.55, marginTop: 14 }}>
        {body}
      </p>
      <div className="inline-flex items-center gap-1.5 mt-5 transition-transform group-hover:translate-x-1" style={{ color: accent, fontWeight: 600, fontSize: "0.88rem" }}>
        {linkLabel} <ArrowRight className="w-3.5 h-3.5" />
      </div>
    </Link>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MultipliersAtlasCard — Section 5 Card 1 redesign (2026-05-25 Ciel spec)
//
// Replaces the static "Americans pay 3–10× the rest of the world" pitch with
// a precomputed per-category multiplier ladder. Numbers come from joining
// scenarios + scenario_costs_cache + international_procedure_prices across 18
// categories (full SQL in ~/ciel-specs-card-redesign/SPEC.md). Median across
// all categories is 16.20× — the "3-10×" headline was conservative; the real
// dataset reads 8.6× (Mental Health) to 23.9× (Infectious Disease).
//
// Each row navigates to /scenarios?category=<slug>. Categories that don't
// have a matching SCENARIO_CATEGORIES key drop to the catalog root.
// ─────────────────────────────────────────────────────────────────────────────

type CategoryMultiplier = {
  category: string;
  /** Maps the spec's display name to a SCENARIO_CATEGORIES key when one
   *  exists. null = no matching catalog filter — link goes to /scenarios. */
  slug: string | null;
  multiplier: number;
  scenariosCompared: number;
  /** Icon component — kept in sync with SCENARIO_CATEGORY_ICONS where slug
   *  maps. Fallback icons chosen by domain for the null-slug categories. */
  icon: React.ElementType;
};

const CATEGORY_MULTIPLIERS: CategoryMultiplier[] = [
  { category: "Infectious Disease",          slug: "immune",         multiplier: 23.93, scenariosCompared: 589, icon: Shield },
  { category: "Cancer & Oncology",           slug: "cancer",         multiplier: 18.64, scenariosCompared: 784, icon: Ribbon },
  { category: "Diabetes & Endocrinology",    slug: "diabetes",       multiplier: 18.56, scenariosCompared: 481, icon: Pill },
  { category: "Digestive & GI",              slug: null,             multiplier: 17.95, scenariosCompared: 652, icon: Stethoscope },
  { category: "Heart & Cardiovascular",      slug: "cardiology",     multiplier: 17.89, scenariosCompared: 603, icon: Heart },
  { category: "Preventive & Screening",      slug: null,             multiplier: 17.77, scenariosCompared: 473, icon: ShieldCheck },
  { category: "Pulmonology",                 slug: "pulmonology",    multiplier: 17.68, scenariosCompared: 517, icon: Wind },
  { category: "Emergency & Critical Care",   slug: "emergency",      multiplier: 17.28, scenariosCompared: 710, icon: Flame },
  { category: "Other Medical Scenarios",     slug: null,             multiplier: 17.28, scenariosCompared: 871, icon: Activity },
  { category: "General Surgery",             slug: "surgery",        multiplier: 16.20, scenariosCompared: 755, icon: Scissors },
  { category: "Women's Health & OB/GYN",     slug: "womens-health",  multiplier: 15.29, scenariosCompared: 428, icon: Baby },
  { category: "Neurological",                slug: null,             multiplier: 15.19, scenariosCompared: 652, icon: Brain },
  { category: "Urology",                     slug: null,             multiplier: 15.18, scenariosCompared: 590, icon: Droplet },
  { category: "Transplant",                  slug: "transplant",     multiplier: 14.59, scenariosCompared: 428, icon: Replace },
  { category: "Pediatric",                   slug: null,             multiplier: 14.58, scenariosCompared: 656, icon: Users },
  { category: "Orthopedic & Joint",          slug: "orthopedic",     multiplier: 12.46, scenariosCompared: 763, icon: Bone },
  { category: "Spine & Back Pain",           slug: "spine",          multiplier: 11.30, scenariosCompared: 587, icon: HeartPulse },
  { category: "Mental Health",               slug: null,             multiplier:  8.63, scenariosCompared: 539, icon: Smile },
];

const MULTIPLIERS_PAGE_SIZE = 5;

function MultipliersAtlasCard({ T }: { T: ReturnType<typeof useThemeTokens> }) {
  const { t } = useTranslation(['homepage']);
  const accent = T.blue;
  const totalPages = Math.ceil(CATEGORY_MULTIPLIERS.length / MULTIPLIERS_PAGE_SIZE);
  const [page, setPage] = useState(0);
  const start = page * MULTIPLIERS_PAGE_SIZE;
  const visible = CATEGORY_MULTIPLIERS.slice(start, start + MULTIPLIERS_PAGE_SIZE);
  // Pad short final page so the list height stays constant — avoids the row
  // height jumping between page 1 (5 rows) and page 4 (3 rows of the 18-row
  // dataset). Padding renders as transparent rows of the same height.
  const padCount = MULTIPLIERS_PAGE_SIZE - visible.length;

  return (
    <div
      style={{
        background: T.cardBg,
        border: `1px solid ${T.cardBorder}`,
        borderRadius: 8,
        padding: 28,
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Blue accent stripe — matches the patriotic rotation across the 4 cards */}
      <div style={{ position: "absolute", top: 0, left: 0, width: 4, height: "100%", background: accent }} />
      <div className="flex items-center gap-2 mb-3" style={{ color: accent }}>
        <Globe className="w-5 h-5" />
        <span style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.12em", fontWeight: 700 }}>
          {t('homepage:homeBeta.atlas.cards.intlEyebrow', { defaultValue: 'See the global comparison' })}
        </span>
      </div>
      <h3 style={{ fontFamily: serif, fontSize: "1.5rem", lineHeight: 1.15, letterSpacing: "-0.01em", color: T.text, margin: 0 }}>
        {t('homepage:homeBeta.atlas.cards.intlTitleNew', { defaultValue: 'Americans pay 16× more — on the same procedure.' })}
      </h3>
      <p style={{ color: T.textMuted, fontSize: "0.88rem", lineHeight: 1.5, marginTop: 10, marginBottom: 16 }}>
        {t('homepage:homeBeta.atlas.cards.intlSubNew', {
          defaultValue: 'Median multiplier across 18 medical categories vs international peers. Click any category to see the underlying scenarios.',
        })}
      </p>
      {/* Paginated 5-row category ladder. Each row icon mirrors the scenario
       *  category icons used elsewhere on the page (Atlas pill bar, hospital
       *  scenario browser) so the visual vocabulary is consistent. */}
      <ul
        className="m-0 p-0 list-none"
        style={{ borderTop: `1px solid ${T.cardBorder}` }}
      >
        {visible.map((row) => {
          const Icon = row.icon;
          const href = row.slug ? `/scenarios?category=${row.slug}` : '/scenarios';
          return (
            <li key={row.category}>
              <Link
                to={href}
                className="flex items-center justify-between gap-3 transition-colors"
                style={{
                  padding: "12px 4px",
                  borderBottom: `1px solid ${T.cardBorder}`,
                  textDecoration: "none",
                  color: T.text,
                  minHeight: 48,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = `${accent}0a`; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              >
                <span className="flex items-center gap-3 flex-1 min-w-0">
                  <span
                    className="flex items-center justify-center shrink-0"
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 6,
                      background: `${accent}14`,
                      color: accent,
                    }}
                  >
                    <Icon className="w-4 h-4" />
                  </span>
                  <span className="truncate" style={{ fontSize: "0.92rem", fontWeight: 500 }}>
                    {row.category}
                  </span>
                </span>
                <span
                  style={{
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    fontSize: "0.92rem",
                    color: accent,
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.multiplier.toFixed(2)}×
                </span>
              </Link>
            </li>
          );
        })}
        {/* Spacer rows so the card doesn't shrink on the last page */}
        {Array.from({ length: padCount }).map((_, i) => (
          <li
            key={`pad-${i}`}
            aria-hidden="true"
            style={{ minHeight: 48, borderBottom: `1px solid transparent` }}
          />
        ))}
      </ul>
      {/* Pagination controls — prev/next + page indicator */}
      <div className="flex items-center justify-between mt-3" style={{ minHeight: 28 }}>
        <button
          type="button"
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          disabled={page === 0}
          className="inline-flex items-center gap-1 transition-opacity"
          aria-label={t('homepage:homeBeta.atlas.cards.intlPrevPage', { defaultValue: 'Previous page' }) as string}
          style={{
            background: "transparent",
            border: `1px solid ${T.cardBorder}`,
            borderRadius: 6,
            padding: "4px 8px",
            color: T.text,
            fontSize: "0.78rem",
            opacity: page === 0 ? 0.35 : 1,
            cursor: page === 0 ? "not-allowed" : "pointer",
          }}
        >
          <ChevronLeft className="w-3.5 h-3.5" />
        </button>
        <span style={{ fontSize: "0.78rem", color: T.textMuted, fontVariantNumeric: "tabular-nums" }}>
          {t('homepage:homeBeta.atlas.cards.intlPageLabel', {
            current: page + 1,
            total: totalPages,
            defaultValue: '{{current}} / {{total}}',
          })}
        </span>
        <button
          type="button"
          onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
          disabled={page >= totalPages - 1}
          className="inline-flex items-center gap-1 transition-opacity"
          aria-label={t('homepage:homeBeta.atlas.cards.intlNextPage', { defaultValue: 'Next page' }) as string}
          style={{
            background: "transparent",
            border: `1px solid ${T.cardBorder}`,
            borderRadius: 6,
            padding: "4px 8px",
            color: T.text,
            fontSize: "0.78rem",
            opacity: page >= totalPages - 1 ? 0.35 : 1,
            cursor: page >= totalPages - 1 ? "not-allowed" : "pointer",
          }}
        >
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
      <Link
        to="/international"
        className="inline-flex items-center gap-1.5 mt-3 group"
        style={{ color: accent, fontWeight: 600, fontSize: "0.88rem", textDecoration: "none" }}
      >
        {t('homepage:homeBeta.atlas.cards.intlLink')} <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
      </Link>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MethodologyAtlasCard — Section 5 Card 4 redesign (2026-05-25 Ciel spec)
//
// Replaces the "federal price-transparency law with no teeth" pitch with a
// transparency-of-OUR-methodology card. Surfaces the 5 most recent
// release_notes (live Supabase query) + a thumbnail of /data-sources +
// two CTAs. White/star accent replaces the medium-blue that Ciel flagged
// as illegible against the dark-navy bg in dark mode.
// ─────────────────────────────────────────────────────────────────────────────

type ReleaseNoteRow = {
  id: string;
  title: string;
  shipped_at: string;
  kind: string | null;
  contributor: string | null;
};

// ─────────────────────────────────────────────────────────────────────────────
// WorstMarkupsAtlasCard — Section 5 Card 2 redesign (2026-05-25)
//
// Replaces the static "1,000%+ over national average" pitch with a paginated
// inline preview of the actual top-25 worst single-procedure markups,
// generated nightly from hospital_procedure_analytics. Each page shows 5
// rows; readers can click through to /hospital/{uuid} or to the dedicated
// /worst-hospital-offenders page for the full sortable table.
//
// Data lives at /data/worst_procedure_markups.json (loaded once on mount,
// cached in memory). Regenerator: scripts/generate_worst_procedure_markups.py.
// ─────────────────────────────────────────────────────────────────────────────

type WorstMarkupItem = {
  hospital_id: string;
  hospital_name: string;
  city: string;
  state: string;
  procedure_code: string;
  procedure_type: string;
  procedure_description: string;
  hospital_price: number;
  national_avg: number;
  markup_pct: number;
};

const WORST_MARKUPS_PAGE_SIZE = 5;
const fmtUsd = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
const fmtMultiplier = (markupPct: number) => {
  // Convert markup % to "X×" — markup_pct = (price - avg) / avg * 100,
  // so multiplier = (price / avg) = (markup_pct / 100) + 1.
  const x = markupPct / 100 + 1;
  return `${x.toLocaleString('en-US', { maximumFractionDigits: 0 })}×`;
};

function WorstMarkupsAtlasCard({ T }: { T: ReturnType<typeof useThemeTokens> }) {
  const { t } = useTranslation(['homepage']);
  const accent = T.red;
  const [items, setItems] = useState<WorstMarkupItem[] | null>(null);
  const [page, setPage] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch('/data/worst_procedure_markups.json');
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const payload = await resp.json();
        if (cancelled) return;
        setItems(Array.isArray(payload?.items) ? payload.items : []);
      } catch {
        if (!cancelled) setItems([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const totalPages = items ? Math.max(1, Math.ceil(items.length / WORST_MARKUPS_PAGE_SIZE)) : 1;
  const start = page * WORST_MARKUPS_PAGE_SIZE;
  const visible = items ? items.slice(start, start + WORST_MARKUPS_PAGE_SIZE) : [];
  const padCount = WORST_MARKUPS_PAGE_SIZE - visible.length;

  return (
    <div
      style={{
        background: T.cardBg,
        border: `1px solid ${T.cardBorder}`,
        borderRadius: 8,
        padding: 28,
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ position: "absolute", top: 0, left: 0, width: 4, height: "100%", background: accent }} />
      <div className="flex items-center gap-2 mb-3" style={{ color: accent }}>
        <Flame className="w-5 h-5" />
        <span style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.12em", fontWeight: 700 }}>
          {t('homepage:homeBeta.atlas.cards.markupsEyebrow', { defaultValue: 'Live offenders' })}
        </span>
      </div>
      <h3 style={{ fontFamily: serif, fontSize: "1.5rem", lineHeight: 1.15, letterSpacing: "-0.01em", color: T.text, margin: 0 }}>
        {t('homepage:homeBeta.atlas.cards.markupsTitle', { defaultValue: 'The worst single-procedure markups.' })}
      </h3>
      <p style={{ color: T.textMuted, fontSize: "0.88rem", lineHeight: 1.5, marginTop: 10, marginBottom: 14 }}>
        {t('homepage:homeBeta.atlas.cards.markupsSubNew', {
          defaultValue: 'Sticker price vs. the national average for the same code. One hospital per row, top 25 nationwide.',
        })}
      </p>
      <ul className="m-0 p-0 list-none" style={{ borderTop: `1px solid ${T.cardBorder}` }}>
        {items === null ? (
          <li style={{ padding: "12px 0", color: T.textMuted, fontSize: "0.82rem" }}>
            {t('homepage:homeBeta.atlas.cards.markupsLoading', { defaultValue: 'Loading offenders…' })}
          </li>
        ) : items.length === 0 ? (
          <li style={{ padding: "12px 0", color: T.textMuted, fontSize: "0.82rem" }}>
            {t('homepage:homeBeta.atlas.cards.markupsEmpty', { defaultValue: 'No data available yet.' })}
          </li>
        ) : (
          <>
            {visible.map((row) => (
              <li key={`${row.hospital_id}-${row.procedure_code}`} style={{ borderBottom: `1px solid ${T.cardBorder}` }}>
                <Link
                  to={`/hospital/${row.hospital_id}`}
                  className="block transition-colors group"
                  style={{ padding: "10px 4px", textDecoration: "none", color: T.text, minHeight: 64 }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = `${accent}0a`; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="truncate flex-1 min-w-0" style={{ fontSize: "0.88rem", fontWeight: 600 }}>
                      {row.procedure_description}
                    </span>
                    <span
                      style={{
                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                        fontSize: "0.92rem",
                        color: accent,
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {fmtMultiplier(row.markup_pct)}
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between gap-3 mt-1" style={{ fontSize: "0.76rem", color: T.textMuted }}>
                    <span className="truncate flex-1 min-w-0">
                      {row.hospital_name} · {row.state}
                    </span>
                    <span style={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                      {fmtUsd(row.hospital_price)} vs {fmtUsd(row.national_avg)}
                    </span>
                  </div>
                </Link>
              </li>
            ))}
            {/* Spacer rows keep card height stable across short pages */}
            {Array.from({ length: padCount }).map((_, i) => (
              <li key={`pad-${i}`} aria-hidden="true" style={{ minHeight: 64, borderBottom: `1px solid transparent` }} />
            ))}
          </>
        )}
      </ul>
      {/* Pagination controls — only show if we actually have multiple pages */}
      {items && items.length > WORST_MARKUPS_PAGE_SIZE && (
        <div className="flex items-center justify-between mt-3" style={{ minHeight: 28 }}>
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            className="inline-flex items-center gap-1 transition-opacity"
            aria-label={t('homepage:homeBeta.atlas.cards.markupsPrevPage', { defaultValue: 'Previous page' }) as string}
            style={{
              background: "transparent",
              border: `1px solid ${T.cardBorder}`,
              borderRadius: 6,
              padding: "4px 8px",
              color: T.text,
              fontSize: "0.78rem",
              opacity: page === 0 ? 0.35 : 1,
              cursor: page === 0 ? "not-allowed" : "pointer",
            }}
          >
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
          <span style={{ fontSize: "0.78rem", color: T.textMuted, fontVariantNumeric: "tabular-nums" }}>
            {t('homepage:homeBeta.atlas.cards.markupsPageLabel', {
              current: page + 1,
              total: totalPages,
              defaultValue: '{{current}} / {{total}}',
            })}
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            className="inline-flex items-center gap-1 transition-opacity"
            aria-label={t('homepage:homeBeta.atlas.cards.markupsNextPage', { defaultValue: 'Next page' }) as string}
            style={{
              background: "transparent",
              border: `1px solid ${T.cardBorder}`,
              borderRadius: 6,
              padding: "4px 8px",
              color: T.text,
              fontSize: "0.78rem",
              opacity: page >= totalPages - 1 ? 0.35 : 1,
              cursor: page >= totalPages - 1 ? "not-allowed" : "pointer",
            }}
          >
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
      <Link
        to="/worst-hospital-offenders"
        className="inline-flex items-center gap-1.5 mt-3 group"
        style={{ color: accent, fontWeight: 600, fontSize: "0.88rem", textDecoration: "none" }}
      >
        {t('homepage:homeBeta.atlas.cards.markupsLink')} <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
      </Link>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ObscureAtlasCard — Section 5 Card 3 redesign (2026-05-26)
//
// Replaces the generic AtlasCard for the "Required to publish, designed to
// obscure" finding. The /data-sources screenshot LITERALLY shows what this
// card is making the argument about (compliance tracker + fine column) so
// it lives here, not on the methodology card. Gold accent stays.
// Same 2-col layout MethodologyAtlasCard used previously: image left,
// content right; collapses to single column under md.
// ─────────────────────────────────────────────────────────────────────────────
function ObscureAtlasCard({ T }: { T: ReturnType<typeof useThemeTokens> }) {
  const { t } = useTranslation(['homepage']);
  const accent = T.gold;
  return (
    <div
      style={{
        background: T.cardBg,
        border: `1px solid ${T.cardBorder}`,
        borderRadius: 8,
        padding: 28,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div style={{ position: "absolute", top: 0, left: 0, width: 4, height: "100%", background: accent }} />
      <div className="grid grid-cols-1 md:grid-cols-[minmax(0,42%)_minmax(0,1fr)] gap-5">
        {/* Thumbnail column — the compliance tracker IS the evidence */}
        <Link
          to="/data-sources"
          className="block group self-start"
          style={{
            border: `1px solid ${T.cardBorder}`,
            borderRadius: 6,
            overflow: "hidden",
            textDecoration: "none",
            color: T.text,
            background: T.bgAlt,
          }}
        >
          <img
            src="/data-sources-preview.png"
            // Re-uses the methodologyPreview* i18n keys — the image content
            // (the /data-sources compliance tracker) hasn't changed; only the
            // card hosting it has. Adding parallel obscurePreview* keys would
            // double the translation surface for identical English text.
            alt={t('homepage:homeBeta.atlas.cards.methodologyPreviewAlt', { defaultValue: 'Preview of the data sources page' })}
            loading="lazy"
            style={{
              display: "block",
              width: "100%",
              height: "auto",
              objectFit: "cover",
              objectPosition: "top",
              opacity: 0.94,
            }}
          />
          <div
            className="flex items-center justify-between gap-2"
            style={{ padding: "8px 10px", fontSize: "0.72rem", color: T.textMuted, borderTop: `1px solid ${T.cardBorder}` }}
          >
            <span className="truncate">
              {t('homepage:homeBeta.atlas.cards.methodologyPreviewCaption', { defaultValue: '9,500+ hospitals · 33 countries · 442K codes' })}
            </span>
            <span
              className="inline-flex items-center gap-1 transition-transform group-hover:translate-x-1 shrink-0"
              style={{ color: accent, fontWeight: 600 }}
            >
              {t('homepage:homeBeta.atlas.cards.methodologyOpenLink', { defaultValue: 'Open →' })}
            </span>
          </div>
        </Link>
        {/* Content column */}
        <div className="flex flex-col min-w-0">
          <div className="flex items-center gap-2 mb-3" style={{ color: accent }}>
            <Database className="w-5 h-5" />
            <span style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.12em", fontWeight: 700 }}>
              {t('homepage:homeBeta.atlas.cards.obscureEyebrow', { defaultValue: 'Read the law' })}
            </span>
          </div>
          <h3 style={{ fontFamily: serif, fontSize: "1.5rem", lineHeight: 1.15, letterSpacing: "-0.01em", color: T.text, margin: 0 }}>
            {t('homepage:homeBeta.atlas.cards.obscureTitle')}
          </h3>
          <p style={{ color: T.textMuted, fontSize: "0.88rem", lineHeight: 1.5, marginTop: 10 }}>
            {t('homepage:homeBeta.atlas.cards.obscureBody')}
          </p>
          <Link
            to="/data-methodology"
            className="inline-flex items-center gap-1.5 mt-4 group self-start"
            style={{ color: accent, fontWeight: 600, fontSize: "0.88rem", textDecoration: "none" }}
          >
            {t('homepage:homeBeta.atlas.cards.obscureLink')}
            <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </div>
  );
}

function MethodologyAtlasCard({ T }: { T: ReturnType<typeof useThemeTokens> }) {
  const { t } = useTranslation(['homepage']);
  const { isDark } = useTheme();
  const accentText = isDark ? '#FAF9F6' : '#1B1B1B';
  const accentBorder = isDark ? '#FAF9F6' : '#1B1B1B';
  const [notes, setNotes] = useState<ReleaseNoteRow[] | null>(null);
  const [totalCount, setTotalCount] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from('release_notes')
          .select('id, title, shipped_at, kind, contributor')
          .eq('visibility', 'public')
          .eq('project_slug', 'americanhealthprices')
          .order('shipped_at', { ascending: false })
          .limit(5);
        if (cancelled) return;
        if (!error && Array.isArray(data)) {
          setNotes(data as ReleaseNoteRow[]);
        } else {
          setNotes([]);
        }
        // Total count via head request — used for the "View all N" CTA.
        const countResp = await supabase
          .from('release_notes')
          .select('id', { count: 'exact', head: true })
          .eq('visibility', 'public')
          .eq('project_slug', 'americanhealthprices');
        if (cancelled) return;
        if (countResp.count != null) setTotalCount(countResp.count);
      } catch {
        if (!cancelled) setNotes([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div
      style={{
        background: T.cardBg,
        border: `1px solid ${T.cardBorder}`,
        borderRadius: 8,
        padding: 28,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div style={{ position: "absolute", top: 0, left: 0, width: 4, height: "100%", background: accentBorder }} />
      {/* Single-column layout — the /data-sources screenshot moved to Card 3
       *  (ObscureAtlasCard) where the compliance tracker visual directly
       *  evidences the "required to publish, designed to obscure" thesis. */}
      <div className="flex items-center gap-2 mb-3" style={{ color: accentText }}>
        <ShieldCheck className="w-5 h-5" />
        <span style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.12em", fontWeight: 700 }}>
          {t('homepage:homeBeta.atlas.cards.methodologyEyebrow', { defaultValue: 'The methodology' })}
        </span>
      </div>
      <h3 style={{ fontFamily: serif, fontSize: "1.5rem", lineHeight: 1.15, letterSpacing: "-0.01em", color: T.text, margin: 0 }}>
        {t('homepage:homeBeta.atlas.cards.methodologyTitle', { defaultValue: 'Built in the open. Audited daily.' })}
      </h3>
      <p style={{ color: T.textMuted, fontSize: "0.88rem", lineHeight: 1.5, marginTop: 10 }}>
        {t('homepage:homeBeta.atlas.cards.methodologySub', {
          defaultValue: 'Every change to how we ingest, normalize, and price ships to /release-notes the day it lands. Data sources are listed in full at /data-sources.',
        })}
      </p>
      <ul className="m-0 mt-4 p-0 list-none" style={{ borderTop: `1px solid ${T.cardBorder}` }}>
        {notes === null ? (
          <li style={{ padding: "12px 0", color: T.textMuted, fontSize: "0.82rem" }}>
            {t('homepage:homeBeta.atlas.cards.methodologyLoading', { defaultValue: 'Loading recent changes…' })}
          </li>
        ) : notes.length === 0 ? (
          <li style={{ padding: "12px 0", color: T.textMuted, fontSize: "0.82rem" }}>
            {t('homepage:homeBeta.atlas.cards.methodologyEmpty', { defaultValue: 'No release notes published yet.' })}
          </li>
        ) : (
          notes.map((n) => {
            const date = new Date(n.shipped_at);
            const dateLabel = Number.isFinite(date.getTime())
              ? date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
              : '';
            return (
              <li key={n.id} style={{ borderBottom: `1px solid ${T.cardBorder}` }}>
                <Link
                  to={`/release-notes#${n.id}`}
                  className="flex items-baseline justify-between gap-3 transition-colors group"
                  style={{ padding: "8px 0", textDecoration: "none", color: T.text }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = accentText; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = T.text; }}
                >
                  <span style={{ fontSize: "0.85rem", lineHeight: 1.35, flex: 1, minWidth: 0 }} className="truncate">
                    {n.title}
                  </span>
                  <span style={{ fontSize: "0.72rem", color: T.textMuted, whiteSpace: "nowrap" }}>{dateLabel}</span>
                </Link>
              </li>
            );
          })
        )}
      </ul>
      <Link
        to="/release-notes"
        className="inline-flex items-center gap-1.5 mt-3 group self-start"
        style={{ color: accentText, fontWeight: 600, fontSize: "0.85rem", textDecoration: "none" }}
      >
        {totalCount != null
          ? t('homepage:homeBeta.atlas.cards.methodologyViewAll', { count: totalCount, defaultValue: 'View all {{count}} changes' })
          : t('homepage:homeBeta.atlas.cards.methodologyViewAllNoCount', { defaultValue: 'View all release notes' })}
        <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
      </Link>
    </div>
  );
}

function SmallTile({
  T, icon, title, body, to,
}: {
  T: ReturnType<typeof useThemeTokens>;
  icon: React.ReactNode; title: string; body: string; to: string;
}) {
  return (
    <Link
      to={to}
      className="flex items-start gap-3 group"
      style={{
        background: T.cardBg,
        border: `1px solid ${T.cardBorder}`,
        borderRadius: 6,
        padding: 18,
        textDecoration: "none",
        color: T.text,
      }}
    >
      <div style={{ color: T.gold, marginTop: 2 }}>{icon}</div>
      <div className="flex-1">
        <div style={{ fontWeight: 600, fontSize: "0.92rem" }}>{title}</div>
        <div style={{ color: T.textMuted, fontSize: "0.82rem", lineHeight: 1.45, marginTop: 3 }}>{body}</div>
      </div>
      <ExternalLink className="w-3.5 h-3.5 opacity-40 group-hover:opacity-100" style={{ color: T.gold }} />
    </Link>
  );
}

function TrustRow({
  T, title, body, last,
}: {
  T: ReturnType<typeof useThemeTokens>;
  title: string; body: string; last?: boolean;
}) {
  return (
    <div
      style={{
        paddingTop: 16, paddingBottom: 16,
        borderBottom: last ? "none" : `1px solid ${T.cardBorder}`,
      }}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <ShieldCheck className="w-4 h-4" style={{ color: T.gold }} />
        <span style={{ fontWeight: 600, fontSize: "0.95rem" }}>{title}</span>
      </div>
      <p style={{ color: T.textMuted, fontSize: "0.87rem", lineHeight: 1.5, marginLeft: 24 }}>
        {body}
      </p>
    </div>
  );
}
